import React, { useEffect, useState, useRef } from 'react';
/**
 * ============================================================================
 * CALLER EXPERIENCE SCREEN — Developer Notes
 * ============================================================================
 *
 * ROUTING LOGIC:
 * - The app starts with ONE default group called "All Callers".
 *   This group handles every incoming call with the same AI settings.
 *
 * - When the user adds additional groups (e.g. Family, Work, VIP):
 *   1. The default group automatically renames to "Everyone Else"
 *   2. A routing explainer banner appears at the top explaining priority
 *   3. Calls are matched to the MOST SPECIFIC group first (by contact match)
 *   4. If a caller is NOT in any specific group → falls back to "Everyone Else"
 *
 * - If the user deletes all custom groups, it reverts to showing "All Callers"
 *   with no explainer banner (single-group mode).
 *
 * GROUP STRUCTURE:
 * - Each group has: dialect, tone, languages[], calendarAccess, contacts[],
 *   includeUnknownNumbers, greeting (prompt), and instructions.
 * - The default group (id='all') cannot be deleted. Disabling it shows a warning.
 * - New custom groups default to: English language, Vexu Voice, no contacts.
 *
 * LANGUAGE SELECTION:
 * - Each group has a languages[] array (multi-select).
 * - At least one language must remain selected (cannot deselect all).
 * - The agent will speak to callers in the selected languages for that group.
 *
 * TEST AGENT:
 * - Requires activation (isActivated prop). If not activated, tapping
 *   "Test Agent" shows a popup with Back + Activate buttons.
 * - When activated, opens a call simulation overlay for that group's settings.
 *
 * CONTACTS:
 * - Each group can have assigned contacts from the user's phonebook.
 * - The default group has no contacts list — it catches all unmatched callers.
 * - includeUnknownNumbers flag controls whether unknown numbers are handled.
 *
 * ============================================================================
 */
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Users, Globe, Volume2, Plus, ChevronRight, ChevronDown, Trash2, Phone, PhoneOff, Calendar, MessageSquare, X, CheckCircle2, Mic, Play, Pause, Sparkles, Shield, Save, Settings, Send, Search, Minus, Siren, BellRing } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface CallerExperienceScreenProps {
  onBack: () => void;
  isActivated?: boolean;
  onNavigate?: (screen: string) => void;
  /** Show the first-run welcome banner (set right after activation). */
  showWelcome?: boolean;
  /** Called when the user dismisses the welcome banner. */
  onDismissWelcome?: () => void;
}
export interface Contact {
  id: string;
  name: string;
  phone: string;
  avatar?: string;
}
interface CallerGroup {
  id: string;
  name: string;
  dialect: string;
  tone: string;
  languages: string[];
  icon: React.ElementType;
  description: string;
  active: boolean;
  contacts: Contact[];
  includeUnknownNumbers: boolean;
  calendarAccess: boolean;
  /** What the AI is allowed to do with the calendar when access is on. */
  calendarPermission: 'book_view' | 'view';
  /** Working days the AI may book/suggest on, e.g. ['Mon','Tue']. */
  availableDays: string[];
  /** Daily booking window start time, 24h "HH:MM". */
  availableStart: string;
  /** Daily booking window end time, 24h "HH:MM". */
  availableEnd: string;
  /** Maximum number of appointments Vexu can book in one hour. */
  slotsPerHour: number;
  /** When on, the AI greets known contacts by their saved first name. */
  personalizeWithName: boolean;
  /** When on, Vexu listens for urgency and alerts you the moment it hears it. */
  emergencyDetection: boolean;
  greeting: string;
  instructions: string;
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VEXU_VOICE = 'Vexu Voice';
interface CustomVoice {
  id: string;
  name: string;
  description: string;
  prompt: string;
}
function describeVoice(prompt: string): string {
  const normalized = prompt.trim().replace(/\.$/, '');
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Custom voice design';
}
const TONES = ['Neutral & Professional', 'Warm & Friendly', 'Business & Professional', 'Premium & Attentive', 'Casual & Relaxed', 'Formal & Reserved'];
const LANGUAGES = ['English', 'Arabic', 'French', 'Spanish', 'German', 'Portuguese', 'Italian', 'Hindi', 'Mandarin', 'Japanese', 'Korean', 'Turkish', 'Russian', 'Dutch', 'Urdu'];
const MOCK_CONTACTS: Contact[] = [{
  id: 'c1',
  name: 'Sarah Connor',
  phone: '+1 (555) 010-1234'
}, {
  id: 'c2',
  name: 'John Smith',
  phone: '+1 (555) 010-5678'
}, {
  id: 'c3',
  name: 'Emma Watson',
  phone: '+1 (555) 010-9012'
}, {
  id: 'c4',
  name: 'Michael Chang',
  phone: '+1 (555) 010-3456'
}, {
  id: 'c5',
  name: 'David Miller',
  phone: '+1 (555) 010-7890'
}, {
  id: 'c6',
  name: 'Lisa Ray',
  phone: '+1 (555) 011-1234'
}, {
  id: 'c7',
  name: 'James Wilson',
  phone: '+1 (555) 011-5678'
}, {
  id: 'c8',
  name: 'Anna Davis',
  phone: '+1 (555) 011-9012'
}, {
  id: 'c9',
  name: 'Robert Taylor',
  phone: '+1 (555) 012-3456'
}, {
  id: 'c10',
  name: 'Maria Garcia',
  phone: '+1 (555) 012-7890'
}, {
  id: 'c11',
  name: 'William Brown',
  phone: '+1 (555) 013-1234'
}, {
  id: 'c12',
  name: 'Jessica Moore',
  phone: '+1 (555) 013-5678'
}, {
  id: 'c13',
  name: 'Thomas Anderson',
  phone: '+1 (555) 013-9012'
}, {
  id: 'c14',
  name: 'Jennifer White',
  phone: '+1 (555) 014-3456'
}, {
  id: 'c15',
  name: 'Richard Lee',
  phone: '+1 (555) 014-7890'
}];
export function CallerExperienceScreen({
  onBack,
  isActivated = false,
  onNavigate,
  showWelcome = false,
  onDismissWelcome
}: CallerExperienceScreenProps) {
  const [showActivateWarning, setShowActivateWarning] = useState(false);
  const [welcomeVisible, setWelcomeVisible] = useState(showWelcome);
  const {
    isLight
  } = useTheme();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Keep the detailed availability controls tucked away until requested.
  const [calendarDetailsGroupId, setCalendarDetailsGroupId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{
    groupId: string;
    field: string;
  } | null>(null);
  const [customVoices, setCustomVoices] = useState<CustomVoice[]>([]);
  const [voiceDeleteConfirmId, setVoiceDeleteConfirmId] = useState<string | null>(null);
  const [voiceDesignGroupId, setVoiceDesignGroupId] = useState<string | null>(null);
  const [voiceDesignPrompt, setVoiceDesignPrompt] = useState('');
  const [voiceName, setVoiceName] = useState('');
  const [voiceDesignStage, setVoiceDesignStage] = useState<'prompt' | 'generating' | 'preview'>('prompt');
  const [designPreviewPlaying, setDesignPreviewPlaying] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<{
    title: string;
    detail: string;
  } | null>(null);
  const [showDefaultWarning, setShowDefaultWarning] = useState(false);
  // Group id awaiting consent before enabling name personalization.
  const [personalizeConsentGroupId, setPersonalizeConsentGroupId] = useState<string | null>(null);
  // Contacts management state
  const [managingContactsGroupId, setManagingContactsGroupId] = useState<string | null>(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  // Text editing modal state
  const [editingText, setEditingText] = useState<{
    groupId: string;
    field: 'greeting' | 'instructions';
    value: string;
  } | null>(null);
  // Language picker state
  const [editingLanguagesGroupId, setEditingLanguagesGroupId] = useState<string | null>(null);
  // Call simulation state
  const [simulatingGroup, setSimulatingGroup] = useState<CallerGroup | null>(null);
  const [callState, setCallState] = useState<'ringing' | 'connected' | 'ended'>('ringing');
  const [messages, setMessages] = useState<Array<{
    sender: 'user' | 'ai';
    text: string;
    timestamp: Date;
  }>>([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeSpeaker, setActiveSpeaker] = useState<'agent' | 'user' | null>(null);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [playingMessageIdx, setPlayingMessageIdx] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const savedToastTimerRef = useRef<number | null>(null);
  const [groups, setGroups] = useState<CallerGroup[]>([{
    id: 'all',
    name: 'All Callers',
    dialect: VEXU_VOICE,
    tone: 'Neutral & Professional',
    languages: ['English', 'Arabic'],
    icon: Users,
    description: 'Handles all incoming calls with the same settings.',
    active: true,
    contacts: [],
    includeUnknownNumbers: true,
    calendarAccess: false,
    calendarPermission: 'book_view',
    availableDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    availableStart: '09:00',
    availableEnd: '17:00',
    slotsPerHour: 1,
    personalizeWithName: false,
    emergencyDetection: true,
    greeting: "Hello, you've reached Vexu.ai assistant. How can I help you?",
    instructions: "Be polite and professional. Collect the caller's name and purpose. Offer to take a message or schedule a callback."
  }]);
  const toggleGroup = (id: string) => {
    const group = groups.find(g => g.id === id);
    if (!group) return;
    if (group.id === 'all' && group.active) {
      setShowDefaultWarning(true);
      return;
    }
    setGroups(prev => prev.map(item => item.id === id ? {
      ...item,
      active: !item.active
    } : item));
    showSaved(group.active ? `${group.name} paused` : `${group.name} enabled`, group.active ? 'Assigned callers will use your fallback experience' : 'This experience is ready for assigned callers');
  };
  const updateGroup = (id: string, field: string, value: string | number | boolean | string[]) => {
    setGroups(prev => prev.map(g => g.id === id ? {
      ...g,
      [field]: value
    } : g));
  };
  const toggleAvailableDay = (groupId: string, day: string) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const has = g.availableDays.includes(day);
      return {
        ...g,
        availableDays: has ? g.availableDays.filter(d => d !== day) : [...g.availableDays, day]
      };
    }));
  };
  const deleteGroup = (id: string) => {
    const group = groups.find(item => item.id === id);
    setGroups(prev => prev.filter(item => item.id !== id));
    setDeleteConfirm(null);
    setExpandedId(null);
    showSaved('Group deleted', `${group?.name ?? 'Caller group'} has been removed`);
  };
  const addContactToGroup = (groupId: string, contact: Contact) => {
    const group = groups.find(item => item.id === groupId);
    setGroups(prev => prev.map(item => item.id === groupId ? {
      ...item,
      contacts: [...item.contacts, contact]
    } : item));
    showSaved('Contact assigned', `${contact.name} now uses ${group?.name ?? 'this caller experience'}`);
  };
  const removeContactFromGroup = (groupId: string, contactId: string) => {
    const group = groups.find(item => item.id === groupId);
    const contact = group?.contacts.find(item => item.id === contactId);
    setGroups(prev => prev.map(item => item.id === groupId ? {
      ...item,
      contacts: item.contacts.filter(entry => entry.id !== contactId)
    } : item));
    showSaved('Contact unassigned', `${contact?.name ?? 'Contact'} will use the fallback experience`);
  };
  const toggleUnknownNumbers = (groupId: string) => {
    const group = groups.find(item => item.id === groupId);
    if (!group) return;
    setGroups(prev => prev.map(item => item.id === groupId ? {
      ...item,
      includeUnknownNumbers: !item.includeUnknownNumbers
    } : item));
    showSaved(group.includeUnknownNumbers ? 'Unknown callers excluded' : 'Unknown callers included', group.includeUnknownNumbers ? `${group.name} now applies only to saved contacts` : `${group.name} now handles callers outside your contacts`);
  };
  const toggleLanguage = (groupId: string, language: string) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const has = g.languages.includes(language);
      const updated = has ? g.languages.filter(l => l !== language) : [...g.languages, language];
      // Don't allow removing all languages
      if (updated.length === 0) return g;
      return {
        ...g,
        languages: updated
      };
    }));
  };
  const addGroup = () => {
    if (!newGroupName.trim()) return;
    const newGroup: CallerGroup = {
      id: `custom-${Date.now()}`,
      name: newGroupName.trim(),
      dialect: VEXU_VOICE,
      tone: 'Neutral & Professional',
      languages: ['English'],
      icon: Users,
      description: `Custom group for ${newGroupName.trim()}.`,
      active: true,
      contacts: [],
      includeUnknownNumbers: false,
      calendarAccess: false,
      calendarPermission: 'book_view',
      availableDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      availableStart: '09:00',
      availableEnd: '17:00',
      slotsPerHour: 1,
      personalizeWithName: false,
      emergencyDetection: true,
      greeting: "Hello, you've reached Vexu.ai assistant. How can I help you?",
      instructions: "Be polite and professional. Collect the caller's name and purpose."
    };
    setGroups(prev => [...prev, newGroup]);
    setNewGroupName('');
    setShowAddGroup(false);
    setExpandedId(newGroup.id);
    showSaved('Group created', `${newGroup.name} is ready to configure`);
  };
  const showSaved = (title: string, detail: string) => {
    if (savedToastTimerRef.current) {
      window.clearTimeout(savedToastTimerRef.current);
    }
    setSavedToast({
      title,
      detail
    });
    savedToastTimerRef.current = window.setTimeout(() => {
      setSavedToast(null);
      savedToastTimerRef.current = null;
    }, 2600);
  };
  const saveEditedText = () => {
    if (!editingText) return;
    const group = groups.find(item => item.id === editingText.groupId);
    updateGroup(editingText.groupId, editingText.field, editingText.value);
    showSaved(editingText.field === 'greeting' ? 'Greeting updated' : 'Instructions updated', `${group?.name ?? 'Caller group'} will use the latest ${editingText.field === 'greeting' ? 'opening message' : 'AI guidance'}`);
    setEditingText(null);
  };
  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, activeSpeaker]);
  // Start call simulation
  const startCallSimulation = (group: CallerGroup) => {
    setSimulatingGroup(group);
    setCallState('ringing');
    setMessages([]);
    setUserInput('');
    setCallStartTime(null);
    setActiveSpeaker(null);
    setPlayingMessageIdx(null);
    // Auto-connect after 2s
    setTimeout(() => {
      setCallState('connected');
      setCallStartTime(new Date());
      setActiveSpeaker('agent');
      // AI opens with greeting
      setTimeout(() => {
        setMessages([{
          sender: 'ai',
          text: group.greeting,
          timestamp: new Date()
        }]);
        setTimeout(() => setActiveSpeaker(null), 2000);
      }, 500);
    }, 2000);
  };
  // Generate AI response based on context
  const generateAIResponse = (userMessage: string, group: CallerGroup): string => {
    const msg = userMessage.toLowerCase();
    // Meeting/schedule keywords
    if ((msg.includes('meeting') || msg.includes('schedule') || msg.includes('time') || msg.includes('appointment')) && group.calendarAccess) {
      return 'I can check the calendar for you. What time works best for you?';
    }
    // Urgent keywords
    if (msg.includes('urgent') || msg.includes('emergency') || msg.includes('important')) {
      if (group.instructions.toLowerCase().includes('interrupt')) {
        return 'I understand this is urgent. Let me try to reach them right away.';
      }
      return "I'll make sure they get this message as a priority. Can you tell me more?";
    }
    // Message/tell keywords
    if (msg.includes('message') || msg.includes('tell them') || msg.includes('let them know')) {
      return "Of course, I'll pass that along. What would you like me to tell them?";
    }
    // Callback keywords
    if (msg.includes('call back') || msg.includes('callback') || msg.includes('return')) {
      return "I'll let them know to call you back. Is this the best number to reach you?";
    }
    // Tone-based responses
    if (group.tone.includes('Warm') || group.tone.includes('Friendly')) {
      const responses = ["I'd be happy to help with that!", 'Sure thing! What else can I help you with?', 'Got it! Anything else you need?'];
      return responses[Math.floor(Math.random() * responses.length)];
    }
    if (group.tone.includes('Premium') || group.tone.includes('Attentive')) {
      return "Absolutely, I'll take care of that right away. Is there anything else I can assist you with?";
    }
    // Default professional response
    return "I've noted that. Is there anything else you'd like me to relay?";
  };
  // Send message
  const handleSendMessage = () => {
    if (!userInput.trim() || !simulatingGroup) return;
    const userMessage = {
      sender: 'user' as const,
      text: userInput.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setActiveSpeaker('user');
    // Simulate user talking duration
    setTimeout(() => {
      setActiveSpeaker('agent');
      setIsTyping(true);
      // AI responds after 1-2s
      setTimeout(() => {
        const aiResponse = generateAIResponse(userMessage.text, simulatingGroup);
        setMessages(prev => [...prev, {
          sender: 'ai',
          text: aiResponse,
          timestamp: new Date()
        }]);
        setIsTyping(false);
        // Simulate AI talking duration based on text length
        setTimeout(() => {
          setActiveSpeaker(null);
        }, Math.max(2000, aiResponse.length * 50));
      }, 1000 + Math.random() * 1000);
    }, 1500);
  };
  // End call
  const endCall = () => {
    setCallState('ended');
    setActiveSpeaker(null);
  };
  const closeSimulation = () => {
    setSimulatingGroup(null);
    setCallState('ringing');
  };
  const togglePlayMessage = (idx: number) => {
    if (playingMessageIdx === idx) {
      setPlayingMessageIdx(null);
    } else {
      setPlayingMessageIdx(idx);
      // Auto stop after 2s
      setTimeout(() => setPlayingMessageIdx(null), 2000);
    }
  };
  // Calculate call duration
  const getCallDuration = () => {
    if (!callStartTime) return '0:00';
    const duration = Math.floor((new Date().getTime() - callStartTime.getTime()) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const beginVoiceDesign = (groupId: string) => {
    setEditingField(null);
    setVoiceDesignGroupId(groupId);
    setVoiceDesignPrompt('');
    setVoiceName('');
    setVoiceDesignStage('prompt');
    setDesignPreviewPlaying(false);
  };
  const generateVoice = () => {
    if (!voiceDesignPrompt.trim()) return;
    setVoiceDesignStage('generating');
    window.setTimeout(() => setVoiceDesignStage('preview'), 1300);
  };
  const saveDesignedVoice = () => {
    if (!voiceDesignGroupId || !voiceName.trim()) return;
    const voice: CustomVoice = {
      id: `voice-${Date.now()}`,
      name: voiceName.trim(),
      description: describeVoice(voiceDesignPrompt),
      prompt: voiceDesignPrompt.trim()
    };
    setCustomVoices(voices => [...voices, voice]);
    updateGroup(voiceDesignGroupId, 'dialect', voice.name);
    setVoiceDesignGroupId(null);
    setEditingField({
      groupId: voiceDesignGroupId,
      field: 'dialect'
    });
    showSaved('Voice created', `${voice.name} is now selected for this caller group`);
  };
  const removeDesignedVoice = (voice: CustomVoice) => {
    const groupsUsingVoice = groups.filter(group => group.dialect === voice.name);
    if (groupsUsingVoice.length) {
      setGroups(currentGroups => currentGroups.map(group => group.dialect === voice.name ? {
        ...group,
        dialect: VEXU_VOICE
      } : group));
    }
    setCustomVoices(voices => voices.filter(item => item.id !== voice.id));
    setVoiceDeleteConfirmId(null);
    showSaved('Voice removed', groupsUsingVoice.length ? `${groupsUsingVoice.length} group${groupsUsingVoice.length === 1 ? '' : 's'} now use Vexu Voice` : 'No caller groups were affected');
  };
  // Picker modal for voice/tone.
  const PickerModal = ({
    title,
    options,
    current,
    onSelect,
    onClose,
    playable = false,
    groupId
  }: {
    title: string;
    options: string[];
    current: string;
    onSelect: (value: string) => void;
    onClose: () => void;
    playable?: boolean;
    groupId: string;
  }) => {
    const [playing, setPlaying] = useState<string | null>(null);
    const [holdingVoiceId, setHoldingVoiceId] = useState<string | null>(null);
    const voiceHoldTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
    const voiceHoldTriggered = useRef(false);
    const clearVoiceHold = () => {
      if (voiceHoldTimer.current) {
        window.clearTimeout(voiceHoldTimer.current);
        voiceHoldTimer.current = null;
      }
      setHoldingVoiceId(null);
    };
    const startVoiceHold = (voiceId: string) => {
      voiceHoldTriggered.current = false;
      setHoldingVoiceId(voiceId);
      voiceHoldTimer.current = window.setTimeout(() => {
        voiceHoldTriggered.current = true;
        setHoldingVoiceId(null);
        setVoiceDeleteConfirmId(voiceId);
      }, 650);
    };
    const togglePlay = (voiceId: string) => {
      setPlaying(active => active === voiceId ? null : voiceId);
      window.setTimeout(() => setPlaying(active => active === voiceId ? null : active), 3200);
    };
    const renderVoiceOption = (id: string, name: string, description: string, category: 'Included' | 'Custom') => {
      const selected = name === current;
      const isPlaying = playing === id;
      return <div key={id} role="radio" aria-checked={selected} tabIndex={0} onPointerDown={() => {
        if (category === 'Custom') startVoiceHold(id);
      }} onPointerUp={clearVoiceHold} onPointerLeave={clearVoiceHold} onPointerCancel={clearVoiceHold} onContextMenu={event => {
        if (category === 'Custom') event.preventDefault();
      }} onClick={() => {
        if (voiceHoldTriggered.current) {
          voiceHoldTriggered.current = false;
          return;
        }
        onSelect(name);
        onClose();
      }} onKeyDown={event => {
        if (event.key === 'ContextMenu' || event.shiftKey && event.key === 'F10') {
          event.preventDefault();
          if (category === 'Custom') setVoiceDeleteConfirmId(id);
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(name);
          onClose();
        }
      }} aria-description={category === 'Custom' ? 'Press and hold to manage this designed voice' : undefined} className={`relative w-full flex items-center gap-3 p-3 rounded-2xl text-left cursor-pointer outline-none transition-transform transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2 ${holdingVoiceId === id ? 'scale-[0.985]' : 'scale-100'} ${selected ? isLight ? 'bg-vexu/5 border-2 border-vexu' : 'bg-vexu/10 border-2 border-vexu' : isLight ? 'bg-slate-50 border-2 border-transparent hover:bg-slate-100' : 'bg-white/[0.03] border-2 border-transparent hover:bg-white/[0.06]'}`} data-id="element-1128" data-mp="2w81sxvaaen1u">
          <button type="button" onPointerDown={event => event.stopPropagation()} onClick={event => {
          event.stopPropagation();
          togglePlay(id);
        }} aria-label={isPlaying ? `Stop ${name} preview` : `Play ${name} preview`} className={`relative w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2 ${isPlaying ? 'bg-vexu text-white' : isLight ? 'bg-white border border-slate-200 text-vexu-600 shadow-sm' : 'bg-white/10 text-vexu'}`} data-id="element-1129" data-mp="2gxk45ejpoqci">
            {isPlaying && <motion.span className="absolute inset-0 rounded-full border-2 border-vexu" animate={{
            scale: [1, 1.28],
            opacity: [0.6, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-1130" data-mp="37q2hbij3n3g4" />}
            {isPlaying ? <Pause className="w-4 h-4 fill-current" data-id="element-1131" data-mp="3vfmzhz7fhwoc" /> : <Play className="w-4 h-4 fill-current ml-0.5" data-id="element-1132" data-mp="3vdiwrkl9n3pn" />}
          </button>
          <div className="flex-1 min-w-0" data-id="element-1133" data-mp="46zn48r61jsx">
            <div className="flex items-center gap-2" data-id="element-1134" data-mp="211vvpgbs670t">
              <span className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1135" data-mp="213fb74pm4e1j">
                {name}
              </span>
              {category === 'Custom' && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isLight ? 'bg-slate-200 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-1136" data-mp="ieujdtwhxjap">
                  Custom
                </span>}
            </div>
            {isPlaying ? <span className="flex items-end gap-0.5 h-3.5 mt-1" aria-label="Preview playing" data-id="element-1137" data-mp="164ow8bgtf7uy">
                {[0, 1, 2, 3, 4, 5].map(bar => <motion.span key={bar} animate={{
              height: [3, 13, 3]
            }} transition={{
              repeat: Infinity,
              duration: 0.8,
              delay: bar * 0.1
            }} className="w-0.5 rounded-full bg-vexu" data-id="element-1138" data-mp="x2z4t5mxo8x6" />)}
              </span> : <p className={`text-[11px] truncate mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1139" data-mp="1gtkuvpx80m3a">
                {description}
              </p>}
          </div>
          <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center ${selected ? 'bg-vexu text-white' : isLight ? 'border border-slate-300' : 'border border-white/15'}`} data-id="element-1140" data-mp="1bsf5a0yuxexz">
            {selected && <CheckCircle2 className="w-4 h-4" data-id="element-1141" data-mp="24hcab2e25f8s" />}
          </span>
          {category === 'Custom' && <span aria-hidden="true" className={`absolute right-3 bottom-2.5 text-[9px] font-medium transition-opacity duration-150 ${holdingVoiceId === id ? 'opacity-100 text-vexu' : 'opacity-0'} ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1142" data-mp="1pr51ledurala">
              Keep holding…
            </span>}
        </div>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1143" data-mp="bs1tepkhgocs">
        <button type="button" aria-label={`Close ${title}`} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-1144" data-mp="lkhcjq7iniz9" />
        <motion.div initial={{
        y: 120,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 120,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 32
      }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden max-h-[72vh] ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1145" data-mp="3ozn1ni9piblk">
          <div className="px-5 pt-5 pb-3 flex justify-between items-start" data-id="element-1146" data-mp="1nv37okfl81ih">
            <div data-id="element-1147" data-mp="1ivgto8rxqh8m">
              <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1148" data-mp="1iuxn1o7qjf9w">
                {title}
              </h3>
              {playable && <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1149" data-mp="18xswlc6uwxna">
                  Preview a voice, or create one made for you
                </p>}
            </div>
            <button type="button" onClick={onClose} aria-label="Close voice picker" className={`p-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1150" data-mp="21mqg1imt10hm">
              <X className="w-5 h-5" data-id="element-1151" data-mp="2kl2pl5hmocdq" />
            </button>
          </div>
          {playable ? <div className="overflow-y-auto max-h-[56vh] px-4 pb-5 space-y-4" data-id="element-1152" data-mp="2stqf47jllupt">
              <div className={`sticky top-0 z-10 pt-1 pb-3 ${isLight ? 'bg-white' : 'bg-[#1a1a1a]'}`} data-id="element-1153" data-mp="2svt8n1jym5za">
                <button type="button" onClick={() => beginVoiceDesign(groupId)} className={`w-full p-4 rounded-2xl text-left border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2 ${isLight ? 'bg-vexu/5 border-vexu/20 hover:bg-vexu/10' : 'bg-vexu/10 border-vexu/25 hover:bg-vexu/15'}`} data-id="element-1154" data-mp="31tvi85hre6y6">
                  <div className="flex items-center gap-3" data-id="element-1155" data-mp="3kl676h3xc9nf">
                    <div className="relative w-11 h-11 rounded-2xl bg-vexu text-white flex items-center justify-center shrink-0" data-id="element-1156" data-mp="3pk91b6id8vgm">
                      <Sparkles className="w-5 h-5" data-id="element-1157" data-mp="3uk3w3l59i56y" />
                      <span className="absolute -bottom-0.5 flex items-end gap-0.5" data-id="element-1158" data-mp="3c9gc2u8v1fy">
                        {[5, 10, 7, 12].map((height, index) => <span key={index} className="w-0.5 bg-white/80 rounded-full" style={{
                      height
                    }} data-id="element-1159" data-mp="39u9qszhbz1bx" data-mpi={index} />)}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-1160" data-mp="3sjpzwb73023y">
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1161" data-mp="3siemrkrqxi06">
                        Design a new voice
                      </p>
                      <p className={`text-[11px] mt-0.5 leading-snug ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1162" data-mp="6aotqa88c57u">
                        Describe the personality and delivery you want.
                      </p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1163" data-mp="237e68pc2oyk1" />
                  </div>
                </button>
              </div>
              <section role="radiogroup" aria-label="Included voice" className="space-y-2" data-id="element-1164" data-mp="no6hzocufycl">
                <p className={`px-1 text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1165" data-mp="dv96412cx7my">
                  Included voice
                </p>
                {renderVoiceOption('vexu-voice', VEXU_VOICE, 'Balanced · clear and natural', 'Included')}
              </section>
              <section role="radiogroup" aria-label="Designed voices" className="space-y-2" data-id="element-1166" data-mp="3r4x37agzyhyv">
                <p className={`px-1 text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1167" data-mp="1paearam1tg97">
                  Designed voices
                </p>
                {customVoices.length ? <>
                    <p className={`px-1 -mt-0.5 text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1168" data-mp="3cd4czft2pxag">
                      Press and hold a voice to manage it.
                    </p>
                    {customVoices.map(voice => renderVoiceOption(voice.id, voice.name, voice.description, 'Custom'))}
                  </> : <p className={`px-1 py-2 text-xs leading-relaxed ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1169" data-mp="2jpi8m5c0uomj">
                    Designed voices you save will appear here.
                  </p>}
              </section>
            </div> : <div className="overflow-y-auto max-h-[40vh] pb-4" data-id="element-1170" data-mp="z6y08rdb2vg7">
              {options.map(opt => <button key={opt} type="button" onClick={() => {
            onSelect(opt);
            onClose();
          }} className={`w-full flex items-center justify-between px-5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-vexu ${opt === current ? isLight ? 'bg-vexu/5' : 'bg-vexu/[0.06]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-1171" data-mp="1460s1a49z568">
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1172" data-mp="pbe6aaiay6u3">
                    {opt}
                  </span>
                  {opt === current && <CheckCircle2 className="w-4 h-4 text-vexu shrink-0 ml-2" data-id="element-1173" data-mp="103mmh7fhu7yh" />}
                </button>)}
            </div>}
        </motion.div>
      </motion.div>;
  };
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-1174" data-mp="19so575dfb3g5">
      {/* Header */}
      <div className={`px-6 pt-6 pb-4 flex items-center gap-3 ${isLight ? 'border-b border-slate-100' : 'border-b border-white/5'}`} data-id="element-1175" data-mp="14ucuytj7uqxe">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-1176" data-mp="h74r8g1frpxh">
          <ArrowLeft className="w-5 h-5" data-id="element-1177" data-mp="m67srqi7je0p" />
        </button>
        <div className="flex-1" data-id="element-1178" data-mp="21ju4xkp8ez4w">
          <h1 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1179" data-mp="21myiv9si198r">
            Caller Experience
          </h1>
          <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1180" data-mp="251rnx671i8kp">
            Configure how AI handles different callers
          </p>
        </div>
        <button onClick={() => setShowAddGroup(true)} className={`p-2 rounded-full border transition-colors ${isLight ? 'text-vexu-600 bg-vexu/10 border-vexu/20' : 'text-vexu bg-vexu/10 border-vexu/20'}`} data-id="element-1181" data-mp="1v5ey4jem8ev8">
          <Plus className="w-5 h-5" data-id="element-1182" data-mp="2nu2mwoykpmdj" />
        </button>
      </div>

      {/* Groups list */}
      <div className="flex-1 overflow-y-auto pb-24" data-id="element-1183" data-mp="2srufll71lcis">
        <div className="p-6 space-y-3" data-id="element-1184" data-mp="2e40ytx6803ym">
          {/* First-run welcome banner (shown right after activation) */}
          <AnimatePresence data-id="element-1185" data-mp="2j1soslnb1g06">
            {welcomeVisible && <motion.div initial={{
            opacity: 0,
            y: -8,
            height: 0
          }} animate={{
            opacity: 1,
            y: 0,
            height: 'auto'
          }} exit={{
            opacity: 0,
            y: -8,
            height: 0
          }} className="overflow-hidden" data-id="element-1186" data-mp="36s56upc9kt4h">
                <div className={`relative rounded-2xl p-4 ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-1187" data-mp="12m7ncdh4zkxn">
                  <button onClick={() => {
                setWelcomeVisible(false);
                onDismissWelcome?.();
              }} className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/10'}`} aria-label="Dismiss" data-id="element-1188" data-mp="1ueybe0oo23bt">
                    <X className="w-4 h-4" data-id="element-1189" data-mp="50tkfq4xprwk" />
                  </button>
                  <div className="flex items-center gap-2.5 mb-2 pr-6" data-id="element-1190" data-mp="52whgu4i8las">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1191" data-mp="3r3kelkvobnwd">
                      <Users className="w-4 h-4" data-id="element-1192" data-mp="4spvjmvm8ajt" />
                    </div>
                    <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1193" data-mp="9matjex7l4j0">
                      Welcome to Caller Experience
                    </h3>
                  </div>
                  <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1194" data-mp="nlakwuc3vxiv">
                    Customize how your AI speaks and behaves for different
                    callers. Start by editing the Default group or create your
                    first caller group. You can always change these settings
                    later.
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
          {/* Routing explainer — only show when multiple groups exist */}
          {groups.length > 1 && <div className={`rounded-xl px-4 py-3 flex items-start gap-2.5 ${isLight ? 'bg-blue-50/80 border border-blue-100' : 'bg-blue-500/[0.06] border border-blue-500/10'}`} data-id="element-1195" data-mp="2376prfngd13n">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isLight ? 'bg-blue-100 text-blue-600' : 'bg-blue-500/20 text-blue-400'}`} data-id="element-1196" data-mp="1bhbt0xx9m94y">
                <span className="text-[10px] font-bold" data-id="element-1197" data-mp="1kgfbbzolykie">?</span>
              </div>
              <p className={`text-[11px] leading-relaxed ${isLight ? 'text-blue-700' : 'text-blue-300/90'}`} data-id="element-1198" data-mp="1p871wa7rkq8j">
                Calls are matched to the most specific group first. If a caller
                isn't in any group, they go to{' '}
                <span className="font-semibold" data-id="element-1199" data-mp="p7v9dc8wnxqa">Everyone Else</span>.
              </p>
            </div>}
          {groups.map(group => {
          const Icon = group.icon;
          const isExpanded = expandedId === group.id;
          return <motion.div key={group.id} layout className={`rounded-2xl overflow-hidden transition-colors duration-300 ${isLight ? 'bg-white border border-slate-200/60 shadow-sm' : 'bg-[#111111]/80 border border-white/5'}`} data-id="element-1200" data-mp="f84g1dw2zors">
                {/* Group header */}
                <button onClick={() => setExpandedId(isExpanded ? null : group.id)} className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-1201" data-mp="3sotxpyjdq8ak">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${group.active ? isLight ? 'bg-vexu/10' : 'bg-vexu/10' : isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1202" data-mp="3jq8hia1dr5cy">
                    <Icon className={`w-5 h-5 ${group.active ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1203" data-mp="393k0q519fpjf" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1204" data-mp="3035aqsdcpm9l">
                    <div className="flex items-center gap-2" data-id="element-1205" data-mp="3nry8mudja46u">
                      <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1206" data-mp="3nsgcac2izkos">
                        {group.id === 'all' ? groups.length === 1 ? 'All Callers' : 'Everyone Else' : group.name}
                      </span>
                      {!group.active && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-500'}`} data-id="element-1207" data-mp="fbv4gylmqxn9">
                          Off
                        </span>}
                      {group.id === 'all' && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/10 text-blue-400'}`} data-id="element-1208" data-mp="28uc2ohih1vq8">
                          Default
                        </span>}
                    </div>
                    <span className={`text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1209" data-mp="1q4ckmel1tgwb">
                      {group.id === 'all' ? groups.length === 1 ? 'Handles all incoming calls' : 'All other callers → default handling' : `${group.contacts.length} contact${group.contacts.length !== 1 ? 's' : ''} → custom handling`}{' '}
                      · {group.dialect}
                    </span>
                  </div>
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1210" data-mp="ojzuokkxu2z">
                    <ChevronDown className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1211" data-mp="eomgk7h5ihgn" />
                  </motion.div>
                </button>

                {/* Expanded details */}
                <AnimatePresence data-id="element-1212" data-mp="oeo3ux1ojvui">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-1213" data-mp="tbxrc75idxfp">
                      <div className={`px-4 pb-4 pt-1 space-y-3 ${isLight ? 'border-t border-slate-100' : 'border-t border-white/5'}`} data-id="element-1214" data-mp="28y1diwgl5mbl">
                        {/* Settings list */}
                        <div className={`rounded-xl overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-white/[0.02]'}`} data-id="element-1215" data-mp="2wtm8csrutid6">
                          {/* Voice */}
                          <button onClick={() => setEditingField({
                      groupId: group.id,
                      field: 'dialect'
                    })} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100 border-b border-slate-100/80' : 'hover:bg-white/[0.04] border-b border-white/[0.04]'}`} data-id="element-1216" data-mp="3paotg31rom90">
                            <div className="flex items-center gap-2.5" data-id="element-1217" data-mp="1dt3cs58u4dj4">
                              <Volume2 className={`w-4 h-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1218" data-mp="yx5jqvu7jpb4" />
                              <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1219" data-mp="py0pw2tlck5a">
                                Voice
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-1220" data-mp="2pawek9lizvyz">
                              <span className={`text-xs font-medium ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1221" data-mp="2p80eofq4ax7w">
                                {group.dialect}
                              </span>
                              <ChevronRight className={`w-3.5 h-3.5 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1222" data-mp="3886cf85u6qkc" />
                            </div>
                          </button>

                          {/* Tone */}
                          <button onClick={() => setEditingField({
                      groupId: group.id,
                      field: 'tone'
                    })} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100 border-b border-slate-100/80' : 'hover:bg-white/[0.04] border-b border-white/[0.04]'}`} data-id="element-1223" data-mp="3d4wrbs5wng46">
                            <div className="flex items-center gap-2.5" data-id="element-1224" data-mp="2m9w45rk2pyof">
                              <Mic className={`w-4 h-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1225" data-mp="2m7ru0rvl03fp" />
                              <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1226" data-mp="2v0whgmzur20m">
                                Tone
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-1227" data-mp="27bk9fqg9gzzw">
                              <span className={`text-xs font-medium ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1228" data-mp="2cbghc0s2xbkq">
                                {group.tone}
                              </span>
                              <ChevronRight className={`w-3.5 h-3.5 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1229" data-mp="3np0qnihmtx82" />
                            </div>
                          </button>

                          {/* Languages */}
                          <button onClick={() => setEditingLanguagesGroupId(group.id)} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100 border-b border-slate-100/80' : 'hover:bg-white/[0.04] border-b border-white/[0.04]'}`} data-id="element-1230" data-mp="3o35zggg4sp0r">
                            <div className="flex items-center gap-2.5" data-id="element-1231" data-mp="356wdl6yzmu67">
                              <Globe className={`w-4 h-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1232" data-mp="315b3tuhr2w08" />
                              <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1233" data-mp="zd6g6v7aakvb">
                                Languages
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-1234" data-mp="1km64b5ej2o8l">
                              <div className="flex gap-1" data-id="element-1235" data-mp="1yem1vqontu24">
                                {group.languages.slice(0, 2).map(lang => <span key={lang} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-1236" data-mp="15qggd1ps8118">
                                    {lang}
                                  </span>)}
                                {group.languages.length > 2 && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isLight ? 'bg-slate-200 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-1237" data-mp="2r65qjq4100bi">
                                    +{group.languages.length - 2}
                                  </span>}
                              </div>
                              <ChevronRight className={`w-3.5 h-3.5 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1238" data-mp="3vc2h55xdunz" />
                            </div>
                          </button>

                          {/* Emergency detection — Vexu listens for urgency and alerts the user. */}
                          <div className={`border-b ${isLight ? 'border-slate-100/80' : 'border-white/[0.04]'}`} data-id="element-1239" data-mp="huv89rb9ktx4">
                            <div className="px-3.5 py-3 flex items-center gap-2.5" data-id="element-1240" data-mp="1ajsdotudmw8g">
                              <Siren className={`w-4 h-4 flex-shrink-0 ${group.emergencyDetection ? isLight ? 'text-red-600' : 'text-red-400' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1241" data-mp="1fbai5rvlzey6" />
                              <button onClick={() => {
                          updateGroup(group.id, 'emergencyDetection', !group.emergencyDetection);
                          showSaved(group.emergencyDetection ? 'Emergency detection off' : 'Emergency detection on', group.emergencyDetection ? `${group.name} calls will no longer be flagged as urgent` : `You'll be alerted instantly if a ${group.name} caller sounds urgent`);
                        }} className="flex-1 min-w-0 text-left" aria-pressed={group.emergencyDetection} data-id="element-1242" data-mp="1k5laglg0jtb0">
                                <span className={`text-sm font-medium block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1243" data-mp="3375dz4kr54ik">
                                  Emergency detection
                                </span>
                                <span className={`text-[11px] block mt-0.5 truncate ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1244" data-mp="2fhb32sjztq0y">
                                  {group.emergencyDetection ? 'Alerts you the moment a caller sounds urgent' : 'Let Vexu spot urgent calls and alert you'}
                                </span>
                              </button>
                              <button onClick={() => {
                          updateGroup(group.id, 'emergencyDetection', !group.emergencyDetection);
                          showSaved(group.emergencyDetection ? 'Emergency detection off' : 'Emergency detection on', group.emergencyDetection ? `${group.name} calls will no longer be flagged as urgent` : `You'll be alerted instantly if a ${group.name} caller sounds urgent`);
                        }} aria-label={group.emergencyDetection ? 'Disable emergency detection' : 'Enable emergency detection'} className={`w-9 h-5 rounded-full flex items-center px-0.5 flex-shrink-0 transition-colors ${group.emergencyDetection ? 'bg-red-500' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-1245" data-mp="2alb4v5ymdwd">
                                <motion.span className="w-4 h-4 rounded-full bg-white shadow-sm" animate={{
                            x: group.emergencyDetection ? 16 : 0
                          }} transition={{
                            type: 'spring',
                            stiffness: 500,
                            damping: 30
                          }} data-id="element-1246" data-mp="thk1s1os0uge" />
                              </button>
                            </div>

                            <AnimatePresence initial={false} data-id="element-1247" data-mp="3huzg557bx23o">
                              {group.emergencyDetection && <motion.div initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: 'auto',
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.22
                        }} className="overflow-hidden" data-id="element-1248" data-mp="3du77ad2dcz7x">
                                  <div className={`mx-3.5 mb-3 flex items-start gap-2 rounded-lg px-2.5 py-2 ${isLight ? 'bg-red-50' : 'bg-red-500/[0.07]'}`} data-id="element-1249" data-mp="l12jp79tiyn">
                                    <BellRing className={`mt-[1px] h-3 w-3 flex-shrink-0 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-1250" data-mp="oczt1gfajkwd" />
                                    <p className={`text-[11px] leading-relaxed ${isLight ? 'text-red-700/90' : 'text-red-200/75'}`} data-id="element-1251" data-mp="10mfop2uu6s0i">
                                      Urgency heard mid-call → instant alert →
                                      call stays flagged in your log.
                                    </p>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>

                          {/* Calendar — its detailed availability setup stays intentionally separate. */}
                          <div className={`border-b ${isLight ? 'border-slate-100/80' : 'border-white/[0.04]'}`} data-id="element-1252" data-mp="1xdqb5py5cenx">
                            <div className="px-3.5 py-3 flex items-center gap-2.5" data-id="element-1253" data-mp="229yjokhu99m4">
                              <Calendar className={`w-4 h-4 flex-shrink-0 ${group.calendarAccess ? 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1254" data-mp="2nlezxgmtekbc" />
                              <button onClick={() => {
                          updateGroup(group.id, 'calendarAccess', !group.calendarAccess);
                          showSaved(group.calendarAccess ? 'Calendar access paused' : 'Calendar access enabled', group.calendarAccess ? `${group.name} will no longer offer times` : `${group.name} can now ${group.calendarPermission === 'book_view' ? 'book meetings' : 'check availability'}`);
                        }} className="flex-1 min-w-0 text-left" aria-pressed={group.calendarAccess} data-id="element-1255" data-mp="2smcocayf4ala">
                                <span className={`text-sm font-medium block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1256" data-mp="1fv89k05m1oz">
                                  Calendar
                                </span>
                                <span className={`text-[11px] block mt-0.5 truncate ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1257" data-mp="3nvptxqhs96rb">
                                  {group.calendarAccess ? group.calendarPermission === 'book_view' ? `Books up to ${group.slotsPerHour} slot${group.slotsPerHour === 1 ? '' : 's'} per hour` : 'Vexu can check availability only' : 'Let Vexu help callers find a time'}
                                </span>
                              </button>
                              <button onClick={() => {
                          updateGroup(group.id, 'calendarAccess', !group.calendarAccess);
                          showSaved(group.calendarAccess ? 'Calendar access paused' : 'Calendar access enabled', group.calendarAccess ? `${group.name} will no longer offer times` : `${group.name} can now ${group.calendarPermission === 'book_view' ? 'book meetings' : 'check availability'}`);
                        }} aria-label={group.calendarAccess ? 'Disable calendar access' : 'Enable calendar access'} className={`w-9 h-5 rounded-full flex items-center px-0.5 flex-shrink-0 transition-colors ${group.calendarAccess ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-1258" data-mp="1xgtusp1fenlb">
                                <motion.span className="w-4 h-4 rounded-full bg-white shadow-sm" animate={{
                            x: group.calendarAccess ? 16 : 0
                          }} transition={{
                            type: 'spring',
                            stiffness: 500,
                            damping: 30
                          }} data-id="element-1259" data-mp="c79fsrgrwxtw" />
                              </button>
                            </div>

                            <AnimatePresence initial={false} data-id="element-1260" data-mp="1istvszkrhotm">
                              {group.calendarAccess && <motion.div initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: 'auto',
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.2
                        }} className="overflow-hidden" data-id="element-1261" data-mp="1dvbm36yx75kk">
                                  <button onClick={() => setCalendarDetailsGroupId(calendarDetailsGroupId === group.id ? null : group.id)} className={`w-full px-3.5 pb-3 flex items-center justify-between text-left ${isLight ? 'text-vexu-700 hover:bg-slate-100/70' : 'text-vexu hover:bg-white/[0.03]'}`} aria-expanded={calendarDetailsGroupId === group.id} data-id="element-1262" data-mp="2ijw4obawd0qb">
                                    <span className="text-xs font-semibold" data-id="element-1263" data-mp="uo7t15l5pbg3">
                                      {calendarDetailsGroupId === group.id ? 'Hide calendar settings' : 'Set availability'}
                                    </span>
                                    <motion.div animate={{
                              rotate: calendarDetailsGroupId === group.id ? 90 : 0
                            }} data-id="element-1264" data-mp="35fhh5gqqvpzt">
                                      <ChevronRight className="w-4 h-4" data-id="element-1265" data-mp="t57m4kf016di" />
                                    </motion.div>
                                  </button>

                                  <AnimatePresence initial={false} data-id="element-1266" data-mp="1gvbsgzjknk87">
                                    {calendarDetailsGroupId === group.id && <motion.div initial={{
                              height: 0,
                              opacity: 0
                            }} animate={{
                              height: 'auto',
                              opacity: 1
                            }} exit={{
                              height: 0,
                              opacity: 0
                            }} transition={{
                              duration: 0.2
                            }} className={`overflow-hidden px-3.5 pb-3.5 pt-0.5 ${isLight ? 'bg-slate-100/60' : 'bg-white/[0.02]'}`} data-id="element-1267" data-mp="16wurrautzr8y">
                                        <div className={`rounded-xl p-3 space-y-3 ${isLight ? 'bg-white border border-slate-200/70' : 'bg-black/20 border border-white/[0.05]'}`} data-id="element-1268" data-mp="24pvkfr27venq">
                                          <div className="flex items-center justify-between gap-3" data-id="element-1269" data-mp="1c802c7j5czmy">
                                            <div data-id="element-1270" data-mp="187zddsyf961n">
                                              <p className={`text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1271" data-mp="37dr9j0ubbdw7">
                                                What Vexu can do
                                              </p>
                                              <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1272" data-mp="3m13wmtzrprop">
                                                {group.calendarPermission === 'book_view' ? 'Check availability and book' : 'Check availability only'}
                                              </p>
                                            </div>
                                            <div className={`grid grid-cols-2 gap-0.5 p-0.5 rounded-lg shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-1273" data-mp="2337ozgny5ez1">
                                              {([{
                                      id: 'book_view',
                                      label: 'Book'
                                    }, {
                                      id: 'view',
                                      label: 'View'
                                    }] as const).map(option => <button key={option.id} onClick={() => {
                                      if (group.calendarPermission === option.id) return;
                                      updateGroup(group.id, 'calendarPermission', option.id);
                                      showSaved('Calendar permission updated', option.id === 'book_view' ? 'Vexu can check availability and book meetings' : 'Vexu can check availability only');
                                    }} className={`px-2 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${group.calendarPermission === option.id ? isLight ? 'bg-white text-vexu-700 shadow-sm' : 'bg-vexu text-white' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1274" data-mp="2zurnsojdpa9o">
                                                  {option.label}
                                                </button>)}
                                            </div>
                                          </div>

                                          {group.calendarPermission === 'book_view' && <>
                                              <div data-id="element-1275" data-mp="1idu47zbioxas">
                                                <p className={`text-[11px] font-semibold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1276" data-mp="1weej4keba8z0">
                                                  Booking days
                                                </p>
                                                <div className="mt-2 grid grid-cols-7 gap-1" data-id="element-1277" data-mp="324c4xd79bkvm">
                                                  {WEEKDAYS.map(day => {
                                        const selected = group.availableDays.includes(day);
                                        return <button key={day} onClick={() => {
                                          toggleAvailableDay(group.id, day);
                                          const total = selected ? group.availableDays.length - 1 : group.availableDays.length + 1;
                                          showSaved('Booking days updated', `${total} day${total === 1 ? '' : 's'} available for appointments`);
                                        }} aria-pressed={selected} aria-label={day} className={`h-8 rounded-lg text-[10px] font-bold transition-colors ${selected ? 'bg-vexu text-white' : isLight ? 'bg-slate-50 border border-slate-200 text-slate-400' : 'bg-white/5 text-zinc-500'}`} data-id="element-1278" data-mp="2ef11fnyivcds">
                                                        {day.slice(0, 1)}
                                                      </button>;
                                      })}
                                                </div>
                                              </div>

                                              <div className="flex items-center gap-2" data-id="element-1279" data-mp="1hoe3pxris56z">
                                                <span className={`text-[11px] font-semibold uppercase tracking-wider shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1280" data-mp="3el4aa2m05nfw">
                                                  Hours
                                                </span>
                                                <input type="time" aria-label="Availability starts" value={group.availableStart} onChange={event => {
                                      updateGroup(group.id, 'availableStart', event.target.value);
                                      showSaved('Availability updated', `Booking window now starts at ${event.target.value}`);
                                    }} className={`flex-1 min-w-0 h-8 rounded-lg border px-1 text-center text-xs font-semibold outline-none ${isLight ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-white/5 border-white/10 text-white'}`} data-id="element-1281" data-mp="2uzhnpmnshmvz" />
                                                <span className={`text-[11px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1282" data-mp="16esotitx9d78">
                                                  –
                                                </span>
                                                <input type="time" aria-label="Availability ends" value={group.availableEnd} onChange={event => {
                                      updateGroup(group.id, 'availableEnd', event.target.value);
                                      showSaved('Availability updated', `Booking window now ends at ${event.target.value}`);
                                    }} className={`flex-1 min-w-0 h-8 rounded-lg border px-1 text-center text-xs font-semibold outline-none ${isLight ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-white/5 border-white/10 text-white'}`} data-id="element-1283" data-mp="11eo4titt20c9" />
                                              </div>

                                              <div className="flex items-center justify-between gap-3" data-id="element-1284" data-mp="a9acxm11v528">
                                                <div data-id="element-1285" data-mp="300fdzufo33ew">
                                                  <p className={`text-[11px] font-semibold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1286" data-mp="2uzhv1fmu25cc">
                                                    Slots / hour
                                                  </p>
                                                  <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1287" data-mp="2zkqq397906m0">
                                                    Maximum appointments Vexu
                                                    can book
                                                  </p>
                                                </div>
                                                <input type="number" inputMode="numeric" min="1" max="60" step="1" aria-label="Maximum appointment slots per hour" value={group.slotsPerHour} onChange={event => {
                                      const inputValue = Number(event.target.value);
                                      const slots = Number.isFinite(inputValue) ? Math.min(60, Math.max(1, inputValue)) : 1;
                                      updateGroup(group.id, 'slotsPerHour', slots);
                                      showSaved('Booking capacity updated', `Vexu can now book up to ${slots} appointment${slots === 1 ? '' : 's'} per hour`);
                                    }} className={`w-14 h-8 shrink-0 rounded-lg border px-1 text-center text-xs font-bold outline-none ${isLight ? 'bg-slate-50 border-slate-200 text-slate-700 focus:border-vexu' : 'bg-white/5 border-white/10 text-white focus:border-vexu'}`} data-id="element-1288" data-mp="1j6qqy0g88kzt" />
                                              </div>
                                            </>}
                                        </div>
                                      </motion.div>}
                                  </AnimatePresence>
                                </motion.div>}
                            </AnimatePresence>
                          </div>

                          {/* Contacts */}
                          <button onClick={() => setManagingContactsGroupId(group.id)} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100' : 'hover:bg-white/[0.04]'}`} data-id="element-1289" data-mp="1urb97zwsr42g">
                            <div className="flex items-center gap-2.5" data-id="element-1290" data-mp="2ab5q385nhi5t">
                              <Users className={`w-4 h-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1291" data-mp="267hiiph0qmkk" />
                              <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1292" data-mp="1wa31ann9ttr7">
                                Contacts
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-1293" data-mp="1mhx9bdx0rcc0">
                              <span className={`text-xs font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1294" data-mp="1ikj560o73uqv">
                                {group.id === 'all' ? 'All other callers' : `${group.contacts.length} assigned`}
                              </span>
                              <ChevronRight className={`w-3.5 h-3.5 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1295" data-mp="3p5406f7sos4j" />
                            </div>
                          </button>

                          {/* Personalize with caller's name */}
                          <button onClick={() => {
                      if (group.personalizeWithName) {
                        updateGroup(group.id, 'personalizeWithName', false);
                        showSaved('Name greeting turned off', 'Known contacts will receive your standard greeting');
                      } else {
                        // Require consent before enabling.
                        setPersonalizeConsentGroupId(group.id);
                      }
                    }} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors border-t ${isLight ? 'hover:bg-slate-100 border-slate-100/80' : 'hover:bg-white/[0.04] border-white/[0.04]'}`} data-id="element-1296" data-mp="31f9qu7moplp1">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-1297" data-mp="pqj2wx9jxy5t">
                              <Sparkles className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1298" data-mp="upvo7dkryx6a" />
                              <div className="min-w-0" data-id="element-1299" data-mp="13jiforhy9e9o">
                                <span className={`text-sm font-medium block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1300" data-mp="13i7g73vjesod">
                                  Greet by name
                                </span>
                                <span className={`text-[11px] block leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1301" data-mp="1nmoe7fv5gjus">
                                  Address known contacts by their first name
                                </span>
                              </div>
                            </div>
                            <div className={`w-9 h-5 rounded-full flex items-center px-0.5 flex-shrink-0 ml-2 transition-colors ${group.personalizeWithName ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-1302" data-mp="26ajmik7zt5gi">
                              <motion.div className="w-4 h-4 rounded-full bg-white shadow-sm" animate={{
                          x: group.personalizeWithName ? 16 : 0
                        }} transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 30
                        }} data-id="element-1303" data-mp="1skpxa0sksj69" />
                            </div>
                          </button>
                        </div>

                        {/* Prompt & Instructions */}
                        <div className={`rounded-xl overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-white/[0.02]'}`} data-id="element-1304" data-mp="3vt0020r56w3z">
                          <button onClick={() => setEditingText({
                      groupId: group.id,
                      field: 'greeting',
                      value: group.greeting
                    })} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100 border-b border-slate-100/80' : 'hover:bg-white/[0.04] border-b border-white/[0.04]'}`} data-id="element-1305" data-mp="4kcdjhh612uu">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-1306" data-mp="34jdm0r54ok3j">
                              <MessageSquare className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1307" data-mp="3jc7edd5i0upm" />
                              <div className="min-w-0" data-id="element-1308" data-mp="3s8zmrj15f7dn">
                                <span className={`text-sm font-medium block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1309" data-mp="3o9z2rx6xpyp2">
                                  Greeting
                                </span>
                                <span className={`text-[11px] block truncate ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1310" data-mp="27upfos2ryl9y">
                                  {group.greeting || 'Tap to set...'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ml-2 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1311" data-mp="29e7l98mcs0ha" />
                          </button>
                          <button onClick={() => setEditingText({
                      groupId: group.id,
                      field: 'instructions',
                      value: group.instructions
                    })} className={`w-full px-3.5 py-3 flex items-center justify-between text-left transition-colors ${isLight ? 'hover:bg-slate-100' : 'hover:bg-white/[0.04]'}`} data-id="element-1312" data-mp="1lmsg7836z52c">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-1313" data-mp="17t15hwdq5f51">
                              <Settings className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1314" data-mp="3l4m3hzk2yqbh" />
                              <div className="min-w-0" data-id="element-1315" data-mp="3c35rjndmag55">
                                <span className={`text-sm font-medium block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1316" data-mp="375w2w9y1y8id">
                                  AI Instructions
                                </span>
                                <span className={`text-[11px] block truncate ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1317" data-mp="2te8x9ji7znmx">
                                  {group.instructions || 'Tap to set...'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ml-2 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1318" data-mp="2ehqgzjv9idgy" />
                          </button>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2" data-id="element-1319" data-mp="1vuhgu4h8aejo">
                          <button onClick={() => toggleGroup(group.id)} className={`flex-1 py-2.5 rounded-xl text-xs font-semibold text-center transition-colors ${group.active ? isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10' : 'bg-vexu/10 text-vexu hover:bg-vexu/20'}`} data-id="element-1320" data-mp="1zthuz9orgt0c">
                            {group.active ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => isActivated ? startCallSimulation(group) : setShowActivateWarning(true)} className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-center flex items-center justify-center gap-1.5 bg-vexu text-white" data-id="element-1321" data-mp="36hwsp1kdoqps">
                            <Play className="w-3.5 h-3.5" data-id="element-1322" data-mp="14lfwo3y1vmq6" /> Test Agent
                          </button>
                          {group.id !== 'all' && <button onClick={() => setDeleteConfirm(group.id)} className={`w-10 py-2.5 rounded-xl flex items-center justify-center transition-colors ${isLight ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'}`} data-id="element-1323" data-mp="gvtz1kxbtfka">
                              <Trash2 className="w-4 h-4" data-id="element-1324" data-mp="22854wvjcivkh" />
                            </button>}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Change confirmation */}
      <AnimatePresence data-id="element-1325" data-mp="4lm68j8jc2nr">
        {savedToast && <motion.div initial={{
        opacity: 0,
        y: 18,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.97
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 30
      }} className="absolute bottom-28 left-4 right-4 z-[70] flex justify-center pointer-events-none" data-id="element-1326" data-mp="4jrkx3tcq8j5">
            <div role="status" aria-live="polite" className={`flex w-full max-w-[330px] items-center gap-3 rounded-2xl border px-3.5 py-3 shadow-[0_12px_32px_rgba(15,23,42,0.18)] backdrop-blur-xl ${isLight ? 'bg-slate-950/95 border-white/15 text-white' : 'bg-white/95 border-white/40 text-slate-900'}`} data-id="element-1327" data-mp="ncvgwvu1kxgx">
              <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-vexu text-white shadow-[0_4px_12px_rgba(0,200,83,0.3)]" data-id="element-1328" data-mp="2misdy7dv3chv"><CheckCircle2 className="h-4.5 w-4.5" data-id="element-1329" data-mp="2hrsh124qsufd" /></div>
              <div className="min-w-0" data-id="element-1330" data-mp="2hq7lpaalt7gg"><p className="text-xs font-bold tracking-[-0.01em]" data-id="element-1331" data-mp="2csfwwp59a1gh">{savedToast.title}</p><p className={`mt-0.5 truncate text-[11px] ${isLight ? 'text-white/65' : 'text-slate-500'}`} data-id="element-1332" data-mp="2cqmmqzvfoz3q">{savedToast.detail}</p></div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Voice/Tone picker */}
      <AnimatePresence data-id="element-1333" data-mp="30ia9r6pkrq0q">
        {editingField && <PickerModal title={editingField.field === 'dialect' ? 'Select Voice' : 'Select Tone'} playable={editingField.field === 'dialect'} options={editingField.field === 'dialect' ? [VEXU_VOICE] : TONES} groupId={editingField.groupId} current={groups.find(g => g.id === editingField.groupId)?.[editingField.field as keyof CallerGroup] as string || ''} onSelect={v => {
        const group = groups.find(item => item.id === editingField.groupId);
        updateGroup(editingField.groupId, editingField.field, v);
        showSaved(editingField.field === 'dialect' ? 'Voice updated' : 'Tone updated', `${group?.name ?? 'Caller group'} now uses ${v}`);
      }} onClose={() => setEditingField(null)} data-id="element-1334" data-mp="30gpdncnm3z0r" />}
      </AnimatePresence>

      {/* Designed voice deletion confirmation */}
      <AnimatePresence data-id="element-1335" data-mp="162i3jn4lldpz">
        {voiceDeleteConfirmId && (() => {
        const voice = customVoices.find(item => item.id === voiceDeleteConfirmId);
        if (!voice) return null;
        const groupsUsingVoice = groups.filter(group => group.dialect === voice.name);
        const groupNames = groupsUsingVoice.map(group => group.id === 'all' && groups.length === 1 ? 'All Callers' : group.id === 'all' ? 'Everyone Else' : group.name);
        return <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-[60] flex items-end justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="delete-voice-title" data-id="element-1336" data-mp="18kk37brei35q">
                <button type="button" aria-label="Cancel voice deletion" onClick={() => setVoiceDeleteConfirmId(null)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-1337" data-mp="unok0qhfqvby" />
                <motion.div initial={{
            y: 80,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: 80,
            opacity: 0
          }} transition={{
            type: 'spring',
            stiffness: 420,
            damping: 32
          }} className={`relative w-full max-w-sm rounded-3xl p-6 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1338" data-mp="2nkabw38ml4l1">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50 text-red-500' : 'bg-red-500/10 text-red-400'}`} data-id="element-1339" data-mp="2rlm147nl7nsp">
                    <Trash2 className="w-5 h-5" data-id="element-1340" data-mp="3ka9s8jv0p7e6" />
                  </div>
                  <h3 id="delete-voice-title" className={`mt-4 text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1341" data-mp="3k86sv97z61ws">
                    Delete “{voice.name}”?
                  </h3>
                  {groupsUsingVoice.length ? <div className="mt-2 space-y-3" data-id="element-1342" data-mp="5gaqmf430wnc">
                      <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1343" data-mp="adu5nh8ltdgc">
                        This voice is currently used by{' '}
                        <span className={`font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1344" data-mp="jbwegiykxae6">
                          {groupNames.join(', ')}
                        </span>
                        . Deleting it will switch{' '}
                        {groupsUsingVoice.length === 1 ? 'that group' : 'those groups'}{' '}
                        to Vexu Voice.
                      </p>
                      <div className={`rounded-xl px-3 py-2.5 text-xs leading-relaxed ${isLight ? 'bg-amber-50 text-amber-800 border border-amber-100' : 'bg-amber-500/10 text-amber-200 border border-amber-500/20'}`} data-id="element-1345" data-mp="1zr8z99hik87e">
                        The caller experience will stay active with Vexu Voice.
                      </div>
                    </div> : <p className={`mt-2 text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1346" data-mp="1gw29cmcjv5zm">
                      This removes the voice from your Designed voices library.
                    </p>}
                  <div className="flex gap-3 mt-6" data-id="element-1347" data-mp="1lvey5cmzen4u">
                    <button type="button" onClick={() => setVoiceDeleteConfirmId(null)} className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/[0.06] text-zinc-200 hover:bg-white/10'}`} data-id="element-1348" data-mp="319b12wmvkjfs">
                      Cancel
                    </button>
                    <button type="button" onClick={() => removeDesignedVoice(voice)} className="flex-1 py-3 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500" data-id="element-1349" data-mp="2zs3zkvcb68py">
                      Delete voice
                    </button>
                  </div>
                </motion.div>
              </motion.div>;
      })()}
      </AnimatePresence>

      {/* Voice Design — kept inside Caller Experience so creation returns to the voice library. */}
      <AnimatePresence data-id="element-1350" data-mp="f4rdzrmtcjpm">
        {voiceDesignGroupId && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className={`absolute inset-0 z-[60] flex flex-col ${isLight ? 'bg-white' : 'bg-[#101010]'}`} role="dialog" aria-modal="true" aria-labelledby="voice-design-title" data-id="element-1351" data-mp="oxluks9aalxz">
            <header className={`px-6 pt-6 pb-4 flex items-center gap-3 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1352" data-mp="61vpux9yvbge">
              <button type="button" onClick={() => {
            setEditingField({
              groupId: voiceDesignGroupId,
              field: 'dialect'
            });
            setVoiceDesignGroupId(null);
          }} aria-label="Back to voice library" className={`p-2 -ml-2 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-1353" data-mp="3sgf2opjamikr">
                <ArrowLeft className="w-5 h-5" data-id="element-1354" data-mp="23vhqispe51ka" />
              </button>
              <div className="flex-1" data-id="element-1355" data-mp="27xcmdhojymqo">
                <h2 id="voice-design-title" className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1356" data-mp="27wba59vsvu7d">
                  Design your voice
                </h2>
                <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1357" data-mp="1g42j4lirbu26">
                  Create a voice for this caller group
                </p>
              </div>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1358" data-mp="sgudu3lbwl05">
                <Sparkles className="w-4 h-4" data-id="element-1359" data-mp="wftgtini27oy" />
              </div>
            </header>

            <main className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1360" data-mp="3m2c4rr3u7lau">
              <AnimatePresence mode="wait" data-id="element-1361" data-mp="3h27vom59dsun">
                {voiceDesignStage === 'prompt' && <motion.div key="voice-prompt" initial={{
              opacity: 0,
              x: 14
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -14
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="space-y-5" data-id="element-1362" data-mp="3vzgw05cji6yi">
                    <div data-id="element-1363" data-mp="rkqqnzpba3cc">
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1364" data-mp="rhutwep79kph">
                        Voice brief
                      </p>
                      <h3 className={`mt-2 text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1365" data-mp="1b694txal6dkh">
                        How should Vexu sound?
                      </h3>
                      <p className={`mt-2 text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1366" data-mp="123hslv9vzdrs">
                        Describe the personality, pace, warmth, accent, and
                        delivery you want callers to hear.
                      </p>
                    </div>
                    <label htmlFor="voice-design-prompt" className="block" data-id="element-1367" data-mp="igtpwwfsw0ja">
                      <span className={`text-sm font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1368" data-mp="4hkba178tehf">
                        Describe your voice
                      </span>
                      <textarea id="voice-design-prompt" autoFocus rows={6} value={voiceDesignPrompt} onChange={event => setVoiceDesignPrompt(event.target.value)} placeholder="For example: Warm, composed, and reassuring. Speak clearly at an unhurried pace, with a polished British accent." className={`mt-2 w-full resize-none rounded-2xl border p-4 text-sm leading-relaxed outline-none transition-colors focus:ring-2 focus:ring-vexu/15 ${isLight ? 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-vexu' : 'bg-white/[0.04] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-vexu'}`} data-id="element-1369" data-mp="2gfs7cc5xynm" />
                    </label>
                    <div className={`rounded-2xl p-4 flex items-start gap-3 ${isLight ? 'bg-slate-50 border border-slate-100' : 'bg-white/[0.03] border border-white/[0.06]'}`} data-id="element-1370" data-mp="z4vixbr0ee2b">
                      <Volume2 className={`w-4 h-4 mt-0.5 shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1371" data-mp="2mrrv3r81r1pm" />
                      <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1372" data-mp="2vsg8qktj301z">
                        You can refine the brief after listening to your
                        generated preview.
                      </p>
                    </div>
                  </motion.div>}

                {voiceDesignStage === 'generating' && <motion.div key="voice-generating" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="min-h-full flex flex-col items-center justify-center text-center pb-20" data-id="element-1373" data-mp="3ei64pb911rvh">
                    <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-1374" data-mp="1y4xgj2xo9izo">
                      <Sparkles className="w-8 h-8 text-vexu" data-id="element-1375" data-mp="274tzcnxdrnmt" />
                      <motion.span className="absolute inset-0 rounded-3xl border-2 border-vexu" animate={{
                  scale: [1, 1.18],
                  opacity: [0.55, 0]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.3,
                  ease: 'easeOut'
                }} data-id="element-1376" data-mp="2776p3n9dr2af" />
                    </div>
                    <h3 className={`mt-6 text-xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1377" data-mp="hsidvpcizqdz">
                      Creating your voice
                    </h3>
                    <p className={`mt-2 max-w-[250px] text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1378" data-mp="2jnzae9r431o9">
                      Vexu is shaping your brief into a natural, call-ready
                      voice.
                    </p>
                    <div className="mt-6 flex items-end justify-center gap-1 h-6" aria-label="Generating voice preview" data-id="element-1379" data-mp="2flumzsr9b1a2">
                      {[7, 17, 11, 22, 14, 19, 9].map((height, index) => <motion.span key={index} className="w-1 rounded-full bg-vexu" animate={{
                  height: [height * 0.45, height, height * 0.55]
                }} transition={{
                  repeat: Infinity,
                  duration: 0.8,
                  delay: index * 0.08
                }} data-id="element-1380" data-mp="25q0yrc46djcg" data-mpi={index} />)}
                    </div>
                  </motion.div>}

                {voiceDesignStage === 'preview' && <motion.div key="voice-preview" initial={{
              opacity: 0,
              x: 14
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -14
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="space-y-5" data-id="element-1381" data-mp="1h1vqjgu2k994">
                    <div data-id="element-1382" data-mp="j6t5d3xyzlz0">
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1383" data-mp="j9p4gvlk0gp1">
                        Preview ready
                      </p>
                      <h3 className={`mt-2 text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1384" data-mp="y4vavjokahiw">
                        Make it yours
                      </h3>
                      <p className={`mt-2 text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1385" data-mp="5g7kxasvb3z0">
                        Listen to the delivery, then give this voice a name
                        before saving it.
                      </p>
                    </div>
                    <div className={`rounded-3xl p-5 border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.03] border-white/[0.08]'}`} data-id="element-1386" data-mp="3iuufbj6ee8gr">
                      <div className="flex items-center gap-4" data-id="element-1387" data-mp="3ars7cftn4bzw">
                        <button type="button" onClick={() => {
                    setDesignPreviewPlaying(playing => !playing);
                    window.setTimeout(() => setDesignPreviewPlaying(false), 3200);
                  }} aria-label={designPreviewPlaying ? 'Stop generated voice preview' : 'Play generated voice preview'} className={`relative w-14 h-14 shrink-0 rounded-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2 ${designPreviewPlaying ? 'bg-vexu text-white' : isLight ? 'bg-white border border-slate-200 text-vexu-600 shadow-sm' : 'bg-white/10 text-vexu'}`} data-id="element-1388" data-mp="35uik96tq4cf8">
                          {designPreviewPlaying && <motion.span className="absolute inset-0 rounded-full border-2 border-vexu" animate={{
                      scale: [1, 1.28],
                      opacity: [0.6, 0]
                    }} transition={{
                      repeat: Infinity,
                      duration: 1.2
                    }} data-id="element-1389" data-mp="ao0pqv8d1sbv" />}
                          {designPreviewPlaying ? <Pause className="w-5 h-5 fill-current" data-id="element-1390" data-mp="1lbyb3qn6of7u" /> : <Play className="w-5 h-5 fill-current ml-0.5" data-id="element-1391" data-mp="1q4reob6x1hic" />}
                        </button>
                        <div className="min-w-0 flex-1" data-id="element-1392" data-mp="11eeipystvdmp">
                          <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1393" data-mp="wm3isvy37ru5">
                            Your generated voice
                          </p>
                          <p className={`mt-1 text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1394" data-mp="mm2sgq1swbgg">
                            Preview the delivery below
                          </p>
                          {designPreviewPlaying && <span className="flex items-end gap-0.5 h-3.5 mt-2" aria-label="Preview playing" data-id="element-1395" data-mp="1afuqdys9d1n8">
                              {[0, 1, 2, 3, 4, 5].map(bar => <motion.span key={bar} animate={{
                        height: [3, 13, 3]
                      }} transition={{
                        repeat: Infinity,
                        duration: 0.8,
                        delay: bar * 0.1
                      }} className="w-0.5 rounded-full bg-vexu" data-id="element-1396" data-mp="3q3uukx9nibsd" />)}
                            </span>}
                        </div>
                      </div>
                      <div className={`mt-4 pt-4 border-t ${isLight ? 'border-slate-200' : 'border-white/[0.08]'}`} data-id="element-1397" data-mp="2ycby9bjbzy1c">
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1398" data-mp="13yji4z6l5akd">
                          Voice brief
                        </p>
                        <p className={`mt-2 text-xs leading-relaxed whitespace-pre-wrap break-words ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1399" data-mp="ugjs1idktic7">
                          {voiceDesignPrompt}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={() => setVoiceDesignStage('prompt')} className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/[0.06] text-zinc-200 hover:bg-white/10'}`} data-id="element-1400" data-mp="1d8cl1csnb3jx">
                      Edit voice brief
                    </button>
                    <label htmlFor="voice-design-name" className="block pt-1" data-id="element-1401" data-mp="3t0a42kpl2j35">
                      <span className={`text-sm font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1402" data-mp="2b2548pertyi4">
                        Voice name <span className="text-vexu" data-id="element-1403" data-mp="265es2d637n1z">*</span>
                      </span>
                      <input id="voice-design-name" type="text" value={voiceName} onChange={event => setVoiceName(event.target.value)} placeholder="e.g. Front Desk" className={`mt-2 w-full rounded-xl border px-4 py-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-vexu/15 ${isLight ? 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-vexu' : 'bg-white/[0.04] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-vexu'}`} data-id="element-1404" data-mp="2k2a8iqoo4gs2" />
                    </label>
                  </motion.div>}
              </AnimatePresence>
            </main>

            {voiceDesignStage !== 'generating' && <footer className={`px-6 py-4 border-t ${isLight ? 'bg-white border-slate-100' : 'bg-[#101010] border-white/5'}`} data-id="element-1405" data-mp="38pwa41ekqowo">
                {voiceDesignStage === 'prompt' ? <button type="button" onClick={generateVoice} disabled={!voiceDesignPrompt.trim()} className="w-full py-3.5 rounded-xl bg-vexu text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-1406" data-mp="3igij3crsch6e">
                    Generate voice
                  </button> : <button type="button" onClick={saveDesignedVoice} disabled={!voiceName.trim()} className="w-full py-3.5 rounded-xl bg-vexu text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-1407" data-mp="a0ysymmevevk">
                    <Save className="w-4 h-4" data-id="element-1408" data-mp="1pp5741i7b6yt" />
                    Save voice
                  </button>}
              </footer>}
          </motion.div>}
      </AnimatePresence>

      {/* Language picker modal */}
      <AnimatePresence data-id="element-1409" data-mp="1bqnk6604dyhy">
        {editingLanguagesGroupId && (() => {
        const group = groups.find(g => g.id === editingLanguagesGroupId);
        if (!group) return null;
        return <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1410" data-mp="11v3sxwwi3nzj">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => {
            setEditingLanguagesGroupId(null);
            showSaved('Languages updated', `${group.languages.length} language${group.languages.length === 1 ? '' : 's'} enabled for ${group.name}`);
          }} data-id="element-1411" data-mp="1xw97rqttheqf" />
                <motion.div initial={{
            y: 200,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: 200,
            opacity: 0
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden max-h-[60vh] ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1412" data-mp="1eyg6su2r7evo">
                  <div className="p-5 pb-3 flex justify-between items-center" data-id="element-1413" data-mp="mgotlixey81x">
                    <div data-id="element-1414" data-mp="hi3slhac7kcf">
                      <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1415" data-mp="hm1do8gh6lyy">
                        Languages
                      </h3>
                      <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1416" data-mp="1599f2fas9avp">
                        Select languages the agent can speak
                      </p>
                    </div>
                    <button onClick={() => {
                setEditingLanguagesGroupId(null);
                showSaved('Languages updated', `${group.languages.length} language${group.languages.length === 1 ? '' : 's'} enabled for ${group.name}`);
              }} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1417" data-mp="1f7owtrnx77sj">
                      <X className="w-5 h-5" data-id="element-1418" data-mp="2102uo8y4yw7i" />
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[40vh] pb-4" data-id="element-1419" data-mp="250ljrv8n9o60">
                    {LANGUAGES.map(lang => {
                const isSelected = group.languages.includes(lang);
                const isLastSelected = isSelected && group.languages.length === 1;
                return <button key={lang} onClick={() => !isLastSelected && toggleLanguage(group.id, lang)} className={`w-full flex items-center justify-between px-5 py-3 transition-colors ${isSelected ? isLight ? 'bg-vexu/5' : 'bg-vexu/[0.06]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-1420" data-mp="fnrmgron9445">
                          <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'} ${isLastSelected ? 'opacity-50' : ''}`} data-id="element-1421" data-mp="3uj1pwqpgscjg">
                            {lang}
                          </span>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-vexu border-vexu' : isLight ? 'border-slate-300' : 'border-zinc-600'}`} data-id="element-1422" data-mp="m2gqa0iq2vt0">
                            {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" data-id="element-1423" data-mp="31t36pbxyb9mp" />}
                          </div>
                        </button>;
              })}
                  </div>
                </motion.div>
              </motion.div>;
      })()}
      </AnimatePresence>

      {/* Add Group Modal */}
      <AnimatePresence data-id="element-1424" data-mp="3minl87t0k2az">
        {showAddGroup && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1425" data-mp="3mjf7gs0q0nol">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAddGroup(false)} data-id="element-1426" data-mp="5a0mbmnxvbbu" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1427" data-mp="x1ya8rr0m2gu">
              <div className="flex justify-between items-center mb-4" data-id="element-1428" data-mp="23jfwxu2nencs">
                <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1429" data-mp="1ykw91o4hv1hs">
                  New Caller Group
                </h3>
                <button onClick={() => setShowAddGroup(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1430" data-mp="2dfspgomgiv5y">
                  <X className="w-5 h-5" data-id="element-1431" data-mp="3jyr0n4uxvkr5" />
                </button>
              </div>
              <input type="text" placeholder="Group name (e.g. Investors, Friends)" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} autoFocus className={`w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors mb-4 ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-2 focus:ring-vexu/10' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:border-vexu focus:ring-1 focus:ring-vexu/20'}`} data-id="element-1432" data-mp="mgrqur4j5s8w" />
              <Button onClick={addGroup} fullWidth disabled={!newGroupName.trim()} data-id="element-1433" data-mp="1e764cm8mt2k1">
                Create Group
              </Button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence data-id="element-1434" data-mp="1j8vf00rxze7l">
        {deleteConfirm && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1435" data-mp="22tdiqq3xsbbm">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} data-id="element-1436" data-mp="1xthnpgd7vu87" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1437" data-mp="1k1b4tmioj952">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50' : 'bg-red-500/10'}`} data-id="element-1438" data-mp="37ttrre3pdyk3">
                <Trash2 className={`w-6 h-6 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-1439" data-mp="2y2qs8gqf9rmx" />
              </div>
              <div data-id="element-1440" data-mp="3qqmo5j863fin">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1441" data-mp="3qr4sl34zh01n">
                  Delete Group?
                </h3>
                <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1442" data-mp="3gs5s47udgapq">
                  This will remove "
                  {groups.find(g => g.id === deleteConfirm)?.name}" and all
                  its settings. Callers in this group will fall back to "All
                  Contacts".
                </p>
              </div>
              <div className="flex gap-3" data-id="element-1443" data-mp="gu5y7cmmvy3i">
                <button onClick={() => setDeleteConfirm(null)} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700' : 'bg-white/5 text-zinc-300'}`} data-id="element-1444" data-mp="gv783drwyejn">
                  Cancel
                </button>
                <button onClick={() => deleteGroup(deleteConfirm)} className="flex-1 py-3 rounded-xl font-medium text-sm bg-red-500 text-white" data-id="element-1445" data-mp="um2odb4qrxx9">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Default Group Warning */}
      <AnimatePresence data-id="element-1446" data-mp="1dkeyaz3h9btw">
        {showDefaultWarning && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1447" data-mp="1dhsqcycocub5">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDefaultWarning(false)} data-id="element-1448" data-mp="2f2a90neb16ca" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1449" data-mp="2tz0z262395p8">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-amber-50' : 'bg-amber-500/10'}`} data-id="element-1450" data-mp="1ii9iv041hs5c">
                <Users className={`w-6 h-6 ${isLight ? 'text-amber-500' : 'text-amber-400'}`} data-id="element-1451" data-mp="3p7utcnqb6bq7" />
              </div>
              <div data-id="element-1452" data-mp="3fagpdnlknfes">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1453" data-mp="3fayqoyn3cjtk">
                  Can't Disable Default Group
                </h3>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1454" data-mp="btgl6xusjowb">
                  "All Contacts" is your fallback group — it handles any caller
                  not assigned to another group. To disable it, please assign
                  another group as the default first.
                </p>
              </div>
              <button onClick={() => setShowDefaultWarning(false)} className={`w-full py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1455" data-mp="2nirb81jbndgr">
                Got it
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Greet-by-name consent */}
      <AnimatePresence data-id="element-1456" data-mp="oarax49c98t5">
        {personalizeConsentGroupId && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1457" data-mp="je0ycqwqsvch">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setPersonalizeConsentGroupId(null)} data-id="element-1458" data-mp="5ej4vy8i4tws" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1459" data-mp="1zr8zki6p2758">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-1460" data-mp="1kbcb3u30vd4r">
                <Sparkles className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1461" data-mp="1odz7876q7k7r" />
              </div>
              <div data-id="element-1462" data-mp="1ybdbl8fdkijp">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1463" data-mp="1ybwvguol5h0d">
                  Greet callers by name
                </h3>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1464" data-mp="39nxm6f50ffir">
                  Your AI will personalize greetings using the first name saved
                  for known contacts — for example,{' '}
                  <span className={`font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1465" data-mp="8f06pq671f5z">
                    "Hi Sarah, how can I help you?"
                  </span>
                </p>
              </div>

              <div className={`rounded-2xl p-3.5 flex items-start gap-2.5 ${isLight ? 'bg-amber-50 border border-amber-100' : 'bg-amber-500/10 border border-amber-500/20'}`} data-id="element-1466" data-mp="idz7kmkpw6if">
                <Shield className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLight ? 'text-amber-500' : 'text-amber-400'}`} data-id="element-1467" data-mp="mdq3f4p3pctt" />
                <p className={`text-[12px] leading-relaxed ${isLight ? 'text-amber-800' : 'text-amber-200/90'}`} data-id="element-1468" data-mp="2t4z3d3hsg1ta">
                  Names come from your own contacts. You're responsible for the
                  names you've saved — if a name is incorrect or contains
                  inappropriate words, the AI may say it aloud.
                </p>
              </div>

              <div className="space-y-2.5 pt-1" data-id="element-1469" data-mp="uu8kaiyoutuw">
                <button onClick={() => {
              updateGroup(personalizeConsentGroupId, 'personalizeWithName', true);
              setPersonalizeConsentGroupId(null);
              showSaved('Name greeting enabled', 'Known contacts will be greeted by their first name');
            }} className="w-full py-3 rounded-xl font-semibold text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-1470" data-mp="yw3njt06jhbb">
                  I understand, enable it
                </button>
                <button onClick={() => setPersonalizeConsentGroupId(null)} className={`w-full py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1471" data-mp="3d168an9628il">
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Activate Warning */}
      <AnimatePresence data-id="element-1472" data-mp="3vti5kvkyqjlv">
        {showActivateWarning && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1473" data-mp="4jjliu84weu2">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowActivateWarning(false)} data-id="element-1474" data-mp="2bcvdtjpg591f" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1475" data-mp="27twno4tqtx96">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-1476" data-mp="2hqhtgsdl7ce2">
                <Play className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1477" data-mp="16c47jt2e970o" />
              </div>
              <div data-id="element-1478" data-mp="11chupkqh9sse">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1479" data-mp="11bzqa0tnw89e">
                  Activate to Test Agent
                </h3>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1480" data-mp="17srpyabkqnaw">
                  You need to activate your Vexu.ai account before you can test
                  the AI agent. Activate a plan to unlock this feature.
                </p>
              </div>
              <div className="flex gap-2" data-id="element-1481" data-mp="ouozrs7aikbi">
                <button onClick={() => setShowActivateWarning(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1482" data-mp="ovgs8u5lc1xk">
                  Back
                </button>
                <button onClick={() => {
              setShowActivateWarning(false);
              onNavigate?.('planSelection');
            }} className="flex-1 py-3 rounded-xl font-semibold text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-1483" data-mp="17gsj4qax16u">
                  Activate
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Simulation Overlay */}
      <AnimatePresence data-id="element-1484" data-mp="5h03cl3x5p2m">
        {simulatingGroup && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 overflow-hidden" data-id="element-1485" data-mp="5fowudj3a37t">
            {/* Ringing State */}
            {callState === 'ringing' && <div className="absolute inset-0 bg-zinc-950 flex flex-col items-center justify-center p-8" data-id="element-1486" data-mp="o6zpaz4gqo1t">
                {/* Futuristic Ringing Animation */}
                <div className="relative w-32 h-32 flex items-center justify-center mb-8" data-id="element-1487" data-mp="j6tsz5n90dde">
                  <motion.div animate={{
              scale: [1, 1.5, 2],
              opacity: [0.5, 0.2, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute inset-0 rounded-full border border-vexu" data-id="element-1488" data-mp="2dmsf2bgi6h8c" />
                  <motion.div animate={{
              scale: [1, 1.2, 1.5],
              opacity: [0.8, 0.4, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.5
            }} className="absolute inset-0 rounded-full border border-vexu" data-id="element-1489" data-mp="1zoc1c0k83qgp" />
                  <div className="relative w-16 h-16 rounded-full bg-vexu/20 flex items-center justify-center backdrop-blur-md border border-vexu/30" data-id="element-1490" data-mp="3u8m8tnormqvs">
                    <Phone className="w-8 h-8 text-vexu" data-id="element-1491" data-mp="3pace1e6ydsap" />
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-1492" data-mp="3kh9ix1mj2ase">
                  {simulatingGroup.name}
                </h2>
                <p className="text-vexu text-sm mb-8 uppercase tracking-widest font-medium" data-id="element-1493" data-mp="3bi4h9mfo61c0">
                  Connecting...
                </p>

                <div className="flex gap-3" data-id="element-1494" data-mp="36hqccd9d77i7">
                  <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs flex items-center gap-2" data-id="element-1495" data-mp="31jyivet2ndbw">
                    <Globe className="w-3 h-3 text-zinc-500" data-id="element-1496" data-mp="2xjy4fu5z324u" />
                    {simulatingGroup.dialect}
                  </div>
                  <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs flex items-center gap-2" data-id="element-1497" data-mp="zqr3649jqtph">
                    <Mic className="w-3 h-3 text-zinc-500" data-id="element-1498" data-mp="2p9qr8gg0o6gy" />
                    {simulatingGroup.tone}
                  </div>
                </div>
              </div>}

            {/* Connected State - Futuristic Voice UI */}
            {callState === 'connected' && <div className="absolute inset-0 bg-zinc-950 flex flex-col" data-id="element-1499" data-mp="2kamhnop6k6q3">
                {/* Live Transcription Background */}
                <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none flex flex-col justify-end pb-40 px-6" data-id="element-1500" data-mp="381jhw47m5fli">
                  <div className="flex flex-col gap-4" data-id="element-1501" data-mp="3cxgzseyiyu0e">
                    {messages.map((msg, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`text-2xl md:text-3xl font-bold leading-tight ${msg.sender === 'ai' ? 'text-vexu text-left' : 'text-blue-400 text-right'}`} data-id="element-1502" data-mp="2z24trbex5h46" data-mpi={idx}>
                        {msg.text}
                      </motion.div>)}
                    <div ref={messagesEndRef} className="h-10" data-id="element-1503" data-mp="1p1so598sb4q6" />
                  </div>
                  {/* Gradient fade at top */}
                  <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-zinc-950 to-transparent" data-id="element-1504" data-mp="11d9hey5dv007" />
                </div>

                {/* Header */}
                <div className="relative z-10 px-6 pt-12 pb-4 flex items-center justify-between" data-id="element-1505" data-mp="1b214oszi0w4s">
                  <div data-id="element-1506" data-mp="16bb0vk6q11c7">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-1507" data-mp="16b16zlik2a18">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-1508" data-mp="il6uj525enhi" />
                      {simulatingGroup.name}
                    </h3>
                    <p className="text-xs text-zinc-400 font-mono mt-1" data-id="element-1509" data-mp="dkj86wov6utm">
                      {callStartTime && getCallDuration()}
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-1510" data-mp="3t8bbw3xsybuv">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-white/5 border border-white/10 text-zinc-400" data-id="element-1511" data-mp="21rrpy62t9jw">
                      {simulatingGroup.dialect}
                    </span>
                  </div>
                </div>

                {/* Center Visualization */}
                <div className="flex-1 relative flex flex-col items-center justify-center" data-id="element-1512" data-mp="g0zu0zxjcxsb">
                  {/* Status Text */}
                  <div className="absolute top-10 left-0 right-0 text-center" data-id="element-1513" data-mp="kt14cgb58efp">
                    <motion.p key={activeSpeaker || 'listening'} initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`text-sm font-medium uppercase tracking-widest ${activeSpeaker === 'agent' ? 'text-vexu' : activeSpeaker === 'user' ? 'text-blue-400' : 'text-zinc-500'}`} data-id="element-1514" data-mp="prm30baqyq21">
                      {activeSpeaker === 'agent' ? 'Agent Speaking...' : activeSpeaker === 'user' ? 'User Speaking...' : 'Listening...'}
                    </motion.p>
                  </div>

                  {/* Concentric Circles */}
                  <div className="relative w-64 h-64 flex items-center justify-center" data-id="element-1515" data-mp="2t7pq99ilqw2p">
                    {/* User Outer Ring (Blue) */}
                    <motion.div animate={activeSpeaker === 'user' ? {
                scale: [1, 1.1, 1.05, 1.15, 1],
                rotate: 180,
                borderRadius: ['50%', '45%', '50%', '48%', '50%']
              } : {
                scale: 1,
                rotate: 0,
                borderRadius: '50%'
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className={`absolute inset-0 rounded-full border-2 transition-colors duration-500 ${activeSpeaker === 'user' ? 'border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.3)]' : 'border-blue-500/20'}`} data-id="element-1516" data-mp="2o83buwr1f9sb" />

                    {/* Agent Inner Ring (Vexu Green) */}
                    <motion.div animate={activeSpeaker === 'agent' ? {
                scale: [1, 1.2, 0.9, 1.1, 1],
                rotate: -180,
                borderRadius: ['50%', '40%', '50%', '45%', '50%']
              } : {
                scale: 1,
                rotate: 0,
                borderRadius: '50%'
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className={`absolute w-40 h-40 rounded-full border-4 transition-colors duration-500 ${activeSpeaker === 'agent' ? 'border-vexu shadow-[0_0_40px_rgba(0,200,83,0.4)]' : 'border-vexu/20'}`} data-id="element-1517" data-mp="2jc1kwsyl0su5" />

                    {/* Core Core */}
                    <div className="absolute w-20 h-20 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center shadow-2xl" data-id="element-1518" data-mp="3obrfccwso4c4">
                      <div className={`w-8 h-8 rounded-full transition-colors duration-500 ${activeSpeaker === 'agent' ? 'bg-vexu animate-pulse' : activeSpeaker === 'user' ? 'bg-blue-500 animate-pulse' : 'bg-zinc-800'}`} data-id="element-1519" data-mp="3tbvv20pvov1a" />
                    </div>
                  </div>
                </div>

                {/* Input Controls */}
                <div className="relative z-10 p-6 bg-gradient-to-t from-zinc-950 via-zinc-950 to-transparent" data-id="element-1520" data-mp="1y7eejbj8kmon">
                  <div className="flex gap-3 mb-6" data-id="element-1521" data-mp="1af7kwk4watsx">
                    <input ref={inputRef} type="text" value={userInput} onChange={e => setUserInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendMessage()} placeholder="Type to simulate user speaking..." className="flex-1 rounded-2xl px-5 py-4 text-sm outline-none bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all backdrop-blur-md" data-id="element-1522" data-mp="15j0e6p4ode9x" />
                    <button onClick={handleSendMessage} disabled={!userInput.trim() || activeSpeaker === 'agent'} className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center disabled:opacity-40 disabled:bg-white/10 transition-all" data-id="element-1523" data-mp="2pv4mhsgyu4e">
                      <Send className="w-5 h-5" data-id="element-1524" data-mp="10cv6m21udzm8" />
                    </button>
                  </div>
                  <button onClick={endCall} className="w-full py-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white font-bold text-sm flex items-center justify-center gap-2 transition-all" data-id="element-1525" data-mp="lfkxv0gl37u7">
                    <PhoneOff className="w-5 h-5" data-id="element-1526" data-mp="3b6zpoyo2lhdm" />
                    End Call
                  </button>
                </div>
              </div>}

            {/* Ended State - Summary & Chat Thread */}
            {callState === 'ended' && <div className={`absolute inset-0 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1527" data-mp="3hm87gl1e6thk">
                {/* WhatsApp-style Header */}
                <div className={`px-4 pt-12 pb-3 flex items-center gap-3 ${isLight ? 'bg-white border-b border-slate-200' : 'bg-[#141414] border-b border-white/5'}`} data-id="element-1528" data-mp="e5rdsqxxmp33">
                  <button onClick={closeSimulation} className={`p-1 rounded-full ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1529" data-mp="47lemh3ifx1x">
                    <ArrowLeft className="w-5 h-5" data-id="element-1530" data-mp="2oy2jzqiyeqe1" />
                  </button>

                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-vexu/20 flex items-center justify-center flex-shrink-0" data-id="element-1531" data-mp="2jyhojq2zt062">
                    <Phone className="w-4 h-4 text-vexu" data-id="element-1532" data-mp="zhqcoyl6eau0" />
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1533" data-mp="14fic0do5gz88">
                    <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1534" data-mp="14gbakrcygifa">
                      +966 50 *** **48
                    </h2>
                    <p className={`text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1535" data-mp="1e7o5jru1vopj">
                      {simulatingGroup.name} · Test Call
                    </p>
                  </div>

                  <div className="flex items-center gap-1" data-id="element-1536" data-mp="1s6nrgrq6hpi0">
                    <button className={`p-2 rounded-full ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-1537" data-mp="1x4p8z87yhgrd">
                      <Phone className="w-4 h-4" data-id="element-1538" data-mp="3tzbxt9y4ct20" />
                    </button>
                    <button className={`p-2 rounded-full ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-1539" data-mp="2qy5kqg7zt4d">
                      <Settings className="w-4 h-4" data-id="element-1540" data-mp="3mvdin71mm91s" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto" data-id="element-1541" data-mp="4lth5xadn59s">
                  {/* Date separator */}
                  <div className="flex justify-center py-3" data-id="element-1542" data-mp="3vwtuzkc04l8n">
                    <span className={`text-[10px] font-medium px-3 py-1 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-500'}`} data-id="element-1543" data-mp="dnjjvz9plqmv">
                      Today
                    </span>
                  </div>

                  {/* Chat Thread */}
                  <div className="px-4 space-y-2 pb-4" data-id="element-1544" data-mp="igcngjtfysxd">
                    {messages.map((msg, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.08
              }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1545" data-mp="xar104u8la4n" data-mpi={idx}>
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2.5 ${msg.sender === 'user' ? 'bg-vexu text-white rounded-tr-sm' : isLight ? 'bg-white text-slate-900 rounded-tl-sm shadow-sm border border-slate-100' : 'bg-[#1A1A1A] text-white rounded-tl-sm border border-white/5'}`} data-id="element-1546" data-mp="sarpmkk913rg">
                          {/* Play button row */}
                          <div className="flex items-start gap-2.5" data-id="element-1547" data-mp="9e092iw3d2r3">
                            <button onClick={() => togglePlayMessage(idx)} className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 transition-colors ${playingMessageIdx === idx ? msg.sender === 'user' ? 'bg-white/20 text-white' : 'bg-vexu text-white' : msg.sender === 'user' ? 'bg-white/10 text-white/80' : isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-1548" data-mp="ecbiisih5b8s">
                              {playingMessageIdx === idx ? <div className="w-2.5 h-2.5 bg-current rounded-sm" data-id="element-1549" data-mp="ax1t2zr689o6" /> : <Play className="w-3 h-3 ml-0.5" data-id="element-1550" data-mp="22dshmwjjg5p" />}
                            </button>

                            <div className="flex-1 min-w-0" data-id="element-1551" data-mp="3t9zpiwor8kdl">
                              {/* Waveform or text */}
                              {playingMessageIdx === idx ? <div className="flex items-center gap-0.5 h-5 my-1" data-id="element-1552" data-mp="3ja8xd1no2hgo">
                                  {[...Array(24)].map((_, i) => <motion.div key={i} animate={{
                          height: ['20%', '100%', '40%', '80%', '20%']
                        }} transition={{
                          duration: 0.5 + Math.random() * 0.5,
                          repeat: Infinity
                        }} className={`w-[3px] rounded-full ${msg.sender === 'user' ? 'bg-white/40' : 'bg-vexu/50'}`} data-id="element-1553" data-mp="398wsppm2kwd1" data-mpi={i} />)}
                                </div> : <p className="text-[13px] leading-relaxed" data-id="element-1554" data-mp="1pxbld1bu57lr">
                                  {msg.text}
                                </p>}
                            </div>
                          </div>

                          {/* Timestamp */}
                          <div className={`flex justify-end mt-1 ${msg.sender === 'user' ? 'text-white/50' : isLight ? 'text-slate-400' : 'text-zinc-600'}`} data-id="element-1555" data-mp="1280pny9ce3eh">
                            <span className="text-[9px]" data-id="element-1556" data-mp="sacvb9fnexwu">
                              {msg.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </span>
                          </div>
                        </div>
                      </motion.div>)}
                  </div>

                  {/* AI Summary Card - at bottom */}
                  <div className="px-4 pb-6" data-id="element-1557" data-mp="3vt0o6850wpsv">
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: messages.length * 0.08 + 0.2
              }} className={`rounded-2xl p-5 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1558" data-mp="3vubsc92drzki">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1559" data-mp="sd3b9rfh93qu">
                        <div className="w-7 h-7 rounded-full bg-vexu/15 flex items-center justify-center" data-id="element-1560" data-mp="16bjtofmpciot">
                          <CheckCircle2 className="w-3.5 h-3.5 text-vexu" data-id="element-1561" data-mp="11d8jg3shw662" />
                        </div>
                        <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1562" data-mp="3v585ic37g4iw">
                          Call Summary
                        </h3>
                      </div>

                      {/* Stats row */}
                      <div className={`flex gap-4 mb-3 pb-3 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1563" data-mp="cr8q7oid6317">
                        <div data-id="element-1564" data-mp="hs4s5wj5nf3z">
                          <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1565" data-mp="moddjrny44jl">
                            Duration
                          </p>
                          <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1566" data-mp="vngvutfagfx1">
                            {callStartTime && getCallDuration()}
                          </p>
                        </div>
                        <div data-id="element-1567" data-mp="2pyq7wg3bcio5">
                          <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1568" data-mp="2l6oz545cta2v">
                            Messages
                          </p>
                          <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1569" data-mp="2bac5g69t6w84">
                            {messages.length}
                          </p>
                        </div>
                        <div data-id="element-1570" data-mp="2yxbucmbwohu7">
                          <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1571" data-mp="2ywa95p16eeyr">
                            Group
                          </p>
                          <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1572" data-mp="1jh3cky1jwraf">
                            {simulatingGroup.name}
                          </p>
                        </div>
                      </div>

                      <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1573" data-mp="2xgmp69raf55y">
                        The caller reached the{' '}
                        <strong data-id="element-1574" data-mp="3q5k7udzev3z8">{simulatingGroup.name}</strong> group. The AI
                        agent handled the call using the{' '}
                        <strong data-id="element-1575" data-mp="3v1izcag1d23v">{simulatingGroup.dialect}</strong> dialect with
                        a <strong data-id="element-1576" data-mp="3h3b0fwzfvqq2">{simulatingGroup.tone.toLowerCase()}</strong>{' '}
                        tone.
                        {messages.some(m => m.text.toLowerCase().includes('meeting') || m.text.toLowerCase().includes('schedule')) ? ' The caller inquired about scheduling a meeting. The agent offered to check calendar availability.' : messages.some(m => m.text.toLowerCase().includes('urgent') || m.text.toLowerCase().includes('emergency')) ? ' The caller reported an urgent matter. The agent prioritized the request accordingly.' : ' The caller left a standard message. The agent collected the necessary details.'}
                      </p>
                    </motion.div>
                  </div>
                </div>

                {/* Bottom bar */}
                <div className={`p-4 ${isLight ? 'bg-white border-t border-slate-200' : 'bg-[#141414] border-t border-white/5'}`} data-id="element-1577" data-mp="1oda30akr2e0x">
                  <Button fullWidth onClick={closeSimulation} data-id="element-1578" data-mp="vpwb4qvws8no">
                    Done
                  </Button>
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Text Editing Modal (Prompt / AI Instructions) */}
      <AnimatePresence data-id="element-1579" data-mp="1u86c1saew89l">
        {editingText && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1580" data-mp="1u9hg7t7rri18">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => {
          saveEditedText();
        }} data-id="element-1581" data-mp="1bgn9tno7oy8m" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1582" data-mp="1gdnhg30pm91u">
              <div className="p-5 pb-3 flex justify-between items-center" data-id="element-1583" data-mp="3tmgc9l0zv8sk">
                <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1584" data-mp="3tkddabhv65bp">
                  {editingText.field === 'greeting' ? 'Edit Prompt' : 'Edit AI Instructions'}
                </h3>
                <button onClick={saveEditedText} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1585" data-mp="3er0b6w1zycmo">
                  <X className="w-5 h-5" data-id="element-1586" data-mp="1cvzo2401b4r7" />
                </button>
              </div>
              <div className="px-5 pb-2" data-id="element-1587" data-mp="lujkaf5fh7e9">
                <p className={`text-xs mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1588" data-mp="qs319mtify9w">
                  {editingText.field === 'greeting' ? 'This is the opening message the AI will use when answering calls for this group.' : 'These instructions guide how the AI behaves during the call — tone, priorities, and actions.'}
                </p>
              </div>
              <div className="px-5 pb-5" data-id="element-1589" data-mp="82d6v0tnteii">
                <textarea autoFocus value={editingText.value} onChange={e => setEditingText({
              ...editingText,
              value: e.target.value
            })} rows={5} className={`w-full text-sm leading-relaxed rounded-xl p-4 resize-none outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-700 focus:border-vexu focus:ring-2 focus:ring-vexu/10' : 'bg-white/5 border border-white/10 text-zinc-300 focus:border-vexu focus:ring-1 focus:ring-vexu/20'}`} data-id="element-1590" data-mp="3q6yc7exr8u69" />
              </div>
              <div className={`px-5 pb-5`} data-id="element-1591" data-mp="3hyakq7c851rj">
                <button onClick={saveEditedText} className="w-full py-3 rounded-xl text-sm font-bold text-center bg-vexu text-white transition-colors hover:bg-vexu-600" data-id="element-1592" data-mp="3hwhgszszwvn8">
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Contacts Management Modal */}
      <AnimatePresence data-id="element-1593" data-mp="4jxhvqze0chl">
        {managingContactsGroupId && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1594" data-mp="2ban8gxghplen">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => {
          setManagingContactsGroupId(null);
          setContactSearchQuery('');
        }} data-id="element-1595" data-mp="19lw5dzxtqujb" />
            <motion.div initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col max-h-[70vh] ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1596" data-mp="1jev1e2vnhncy">
              {(() => {
            const group = groups.find(g => g.id === managingContactsGroupId);
            if (!group) return null;
            const unassignedContacts = MOCK_CONTACTS.filter(c => !group.contacts.some(assigned => assigned.id === c.id)).filter(c => c.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) || c.phone.includes(contactSearchQuery));
            return <>
                    <div className={`p-5 pb-4 flex justify-between items-center border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1597" data-mp="2xkq46fcmvbmm">
                      <div data-id="element-1598" data-mp="3l8hexeiizgls">
                        <h3 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1599" data-mp="3q1j0g5d8meec">
                          Manage Contacts
                        </h3>
                        <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1600" data-mp="3uz7uq62e8zy3">
                          {group.name}
                        </p>
                      </div>
                      <button onClick={() => {
                  setManagingContactsGroupId(null);
                  setContactSearchQuery('');
                }} className={`p-2 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1601" data-mp="3lzkwft4yoa99">
                        <X className="w-5 h-5" data-id="element-1602" data-mp="32b2b5mn2tgzu" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 space-y-6" data-id="element-1603" data-mp="2y6umtqkj6325">
                      {/* Unknown Numbers Toggle */}
                      <div className={`flex items-center justify-between p-4 rounded-2xl border ${isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/[0.02] border-white/5'}`} data-id="element-1604" data-mp="18qifwzqnzpin">
                        <div className="flex-1 pr-4" data-id="element-1605" data-mp="1hqykkcj592p3">
                          <h4 className={`text-sm font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1606" data-mp="1hpdpmlsx3hqp">
                            Include Unknown Numbers
                          </h4>
                          <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1607" data-mp="u2nnlkgkjn63">
                            Apply this experience to callers not in your
                            contacts.
                          </p>
                        </div>
                        <button onClick={() => toggleUnknownNumbers(group.id)} className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${group.includeUnknownNumbers ? 'bg-vexu' : isLight ? 'bg-slate-300' : 'bg-zinc-700'}`} data-id="element-1608" data-mp="69ys9gwsqnv2">
                          <motion.div animate={{
                      x: group.includeUnknownNumbers ? 24 : 2
                    }} className="absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-1609" data-mp="2mq3i0ej84dvw" />
                        </button>
                      </div>

                      {group.id === 'all' && <div className={`p-4 rounded-2xl border ${isLight ? 'bg-blue-50 border-blue-100' : 'bg-blue-500/10 border-blue-500/20'}`} data-id="element-1610" data-mp="35phfh6g5w6r5">
                          <p className={`text-xs leading-relaxed ${isLight ? 'text-blue-700' : 'text-blue-300'}`} data-id="element-1611" data-mp="1baev1n5yzev3">
                            This is the fallback group. It automatically applies
                            to anyone who isn't assigned to another group. You
                            cannot manually assign contacts here.
                          </p>
                        </div>}

                      {group.id !== 'all' && <>
                          {/* Assigned Contacts */}
                          <div data-id="element-1612" data-mp="dh5pvg6dio5n">
                            <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1613" data-mp="dg43wgntkh95">
                              Assigned Contacts ({group.contacts.length})
                            </h4>
                            <div className="space-y-2" data-id="element-1614" data-mp="9hy0nim6wjsz">
                              <AnimatePresence initial={false} data-id="element-1615" data-mp="1yui7yyl5awp5">
                                {group.contacts.map(contact => <motion.div key={contact.id} initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-white border-slate-100 shadow-sm' : 'bg-[#222] border-white/5'}`} data-id="element-1616" data-mp="23t3c393iq0iv">
                                    <div className="flex items-center gap-3" data-id="element-1617" data-mp="33ugkqpy925pp">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-1618" data-mp="2z2opi245fbt8">
                                        {contact.name.split(' ').map(n => n[0]).join('')}
                                      </div>
                                      <div data-id="element-1619" data-mp="3pawak740br0t">
                                        <div className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1620" data-mp="3ubk16robq5ui">
                                          {contact.name}
                                        </div>
                                        <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1621" data-mp="cwd8vdiebtpk">
                                          {contact.phone}
                                        </div>
                                      </div>
                                    </div>
                                    <button onClick={() => removeContactFromGroup(group.id, contact.id)} className={`p-2 rounded-full transition-colors ${isLight ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`} data-id="element-1622" data-mp="1c6tifulty7ha">
                                      <Minus className="w-4 h-4" data-id="element-1623" data-mp="oemhsj92nec2" />
                                    </button>
                                  </motion.div>)}
                              </AnimatePresence>
                              {group.contacts.length === 0 && <p className={`text-sm text-center py-4 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1624" data-mp="27dapbjliwaxt">
                                  No contacts assigned yet.
                                </p>}
                            </div>
                          </div>

                          {/* Add Contacts */}
                          <div data-id="element-1625" data-mp="2v4pvxokc1eev">
                            <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1626" data-mp="2v3ohrb80sbsx">
                              Add Contacts
                            </h4>

                            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border mb-3 transition-colors ${isLight ? 'bg-white border-slate-200 focus-within:border-vexu focus-within:ring-2 focus-within:ring-vexu/10' : 'bg-black/20 border-white/10 focus-within:border-vexu focus-within:ring-1 focus-within:ring-vexu/20'}`} data-id="element-1627" data-mp="15ps06il7cnk4">
                              <Search className={`w-4 h-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1628" data-mp="ipso0abpdct9" />
                              <input type="text" placeholder="Search contacts..." value={contactSearchQuery} onChange={e => setContactSearchQuery(e.target.value)} className={`flex-1 bg-transparent border-none outline-none text-sm ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-1629" data-mp="3m6rd4yu03rel" />
                            </div>

                            <div className="space-y-2" data-id="element-1630" data-mp="2zylux6bm6bnv">
                              <AnimatePresence initial={false} data-id="element-1631" data-mp="2zvzoxb4dg47r">
                                {unassignedContacts.map(contact => <motion.div key={contact.id} initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-white border-slate-100 shadow-sm' : 'bg-[#222] border-white/5'}`} data-id="element-1632" data-mp="2qww4o3tgxiro">
                                    <div className="flex items-center gap-3" data-id="element-1633" data-mp="1ol4fq7ycc8g8">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-1634" data-mp="1oj1fkv3h4yxs">
                                        {contact.name.split(' ').map(n => n[0]).join('')}
                                      </div>
                                      <div data-id="element-1635" data-mp="1xl0xrqq8692b">
                                        <div className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1636" data-mp="1xjzl5htk9eih">
                                          {contact.name}
                                        </div>
                                        <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1637" data-mp="el1pkzfmkkto">
                                          {contact.phone}
                                        </div>
                                      </div>
                                    </div>
                                    <button onClick={() => addContactToGroup(group.id, contact)} className={`p-2 rounded-full transition-colors ${isLight ? 'text-vexu-600 bg-vexu/10 hover:bg-vexu/20' : 'text-vexu bg-vexu/10 hover:bg-vexu/20'}`} data-id="element-1638" data-mp="3rw0r8avjb6xr">
                                      <Plus className="w-4 h-4" data-id="element-1639" data-mp="1c3bk0d7bwvya" />
                                    </button>
                                  </motion.div>)}
                              </AnimatePresence>
                              {unassignedContacts.length === 0 && <p className={`text-sm text-center py-4 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1640" data-mp="2gddj53pabh8n">
                                  No more contacts available.
                                </p>}
                            </div>
                          </div>
                        </>}
                    </div>
                  </>;
          })()}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}